import { useState, useMemo, useEffect, useCallback, useRef, createContext, useContext } from "react";
import {
  augcStats as mockAugcStats,
  styleCollections as mockStyleCollections,
  styleTemplates as mockStyleTemplates,
  pureSwapTemplates as mockPureSwapTemplates,
  styleColors,
  sceneTypeColors,
  getStyleColor,
  getStyleLabel,
  getSceneTypeColor,
  getSceneTypeLabel,
  styleRankDataToday as mockStyleRankDataToday,
  styleRankData7d as mockStyleRankData7d,
  styleRankData30d as mockStyleRankData30d,
  sceneHeatDataToday as mockSceneHeatDataToday,
  sceneHeatData7d as mockSceneHeatData7d,
  sceneHeatData30d as mockSceneHeatData30d,
  dailyTrendDataToday as mockDailyTrendDataToday,
  dailyTrendData7d as mockDailyTrendData7d,
  dailyTrendData30d as mockDailyTrendData30d,
  mockUserPhotos as mockUserPhotosSeed,
} from "@/mocks/augc";
import type { StyleCollection, StyleTemplate, PureSwapTemplate } from "@/mocks/augc";
import { useApi } from "@/hooks/useApi";
import { apiFetch } from "@/lib/api";
import { toast } from "@/components/ui/feedback";
import AugcHistoryPage from "../augc-history/page";
import BeautyPresetsTab, { type BeautyPresetLeaveHandler } from "./BeautyPresetsTab";
import {
  WanTemplateGenerationFields,
  LocalSupplementalItem,
  OutfitTagGroup,
  isAvailableOutfitTypeTag,
  getAvailableBodyCoverageTags,
  isAvailableBodyCoverageTag,
  getDefaultFullBodyTag,
  getTemplateBodyCoverageTag,
} from "./WanTemplateGenerationFields";
import {
  ActionPromptTagGroup,
  ActionPromptTag,
  ActionPrompt,
  ActionPromptRelation,
} from "./actionPromptTypes";
import { ActionPromptLibrary } from "./ActionPromptLibrary";
import { WanPromptGuidance, WanActionPromptSelect } from "./WanTemplateGenerationFields";
import { useActionPromptsCatalog } from "./useActionPromptsCatalog";
import type { OutfitTypeTag, SupplementalImage } from "@/mocks/augc";
/* ═══════════════════  THREE-LEVEL CATEGORY (DB-backed)  ═══════════════════ */
// faceswap_templates 三级分类：来源 /faceswap/categories,可在页面上做 CRUD。
// 一级=category / 二级=subcategory / 三级=subsubcategory(可选)。同层同名唯一。
// sub2Map 的 key 用 `${category}:::${subcategory}` 拼接,值是该二级下的三级名字数组;
// 空数组表示该二级下没有三级(不显示第三级下拉)。
interface FSCategoryNode {
  id: string;
  name: string;
  sortOrder: number;
  enabled: boolean;
  children?: FSCategoryNode[];
}
interface FSCategoryCtx {
  tree: FSCategoryNode[];
  topCategories: string[];
  subMap: Record<string, string[]>;
  sub2Map: Record<string, string[]>;
  reload: () => void;
}
const CategoryTreeContext = createContext<FSCategoryCtx>({
  tree: [], topCategories: [], subMap: {}, sub2Map: {}, reload: () => {},
});
export function sub2Key(cat: string, sub: string): string { return `${cat}:::${sub}`; }
function useCategoryTree(): FSCategoryCtx {
  return useContext(CategoryTreeContext);
}

/* ═══════════════════  FACESWAP STYLE HELPERS  ═══════════════════ */
type FaceSwapStyle = "chibi" | "photo";

const FACESWAP_STYLE_OPTIONS = [
  { value: "photo", label: "纯换脸", description: "真人模板" },
  { value: "chibi", label: "卡通/IP", description: "Q版卡通风格" },
];

const FACESWAP_STYLE_LABELS: Record<string, string> = {
  photo: "纯换脸",
  chibi: "卡通/IP",
};

function defaultFaceswapStyle(category: string): FaceSwapStyle {
  void category;
  return "chibi";
}

type GenerationProvider = "wan" | "facefusion";
type ManagedStyleTemplate = StyleTemplate & {
  generationProvider?: GenerationProvider;
  outfitType?: OutfitTypeTag | null;
  outfitFilterTags?: OutfitTypeTag[];
  supplementalImages?: SupplementalImage[];
  actionPrompts?: ActionPromptRelation[];
};
const GENERATION_PROVIDER_OPTIONS: { value: GenerationProvider; label: string; description: string }[] = [
  { value: "facefusion", label: "FaceFusion", description: "旧 photo 模板及现有卡通流程" },
  { value: "wan", label: "万相", description: "写实旅拍服装生成" },
];

function templateGenerationProvider(template: { generationProvider?: string; style?: string }): GenerationProvider {
  return template.generationProvider === "wan" ? "wan" : "facefusion";
}

interface FaceswapStats {
  totalTemplates: number;
  enabledTemplates: number;
  totalGenerated: number;
  todayGenerated: number;
}

interface FaceswapTrend {
  today: { hour: string; value: number }[];
  d7: { hour: string; value: number }[];
  d30: { hour: string; value: number }[];
}

const FALLBACK_FACESWAP_STATS: FaceswapStats = {
  ...mockAugcStats,
  totalGenerated: 0,
};

/* ═══════════════════  ANIMATED STAT CARD  ═══════════════════ */
function AnimatedStat({ value, label, colorClass, bgClass, borderClass, delay }: {
  value: number; label: string; colorClass: string; bgClass: string; borderClass: string; delay: number;
}) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => {
      const duration = 900;
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 4);
        setDisplay(Math.round(eased * value));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return (
    <div className={`${bgClass} border ${borderClass} rounded-xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default`}>
      <div className="text-xs text-gray-500 mb-1.5">{label}</div>
      <div className={`text-3xl font-bold ${colorClass} tabular-nums`}>{display.toLocaleString()}</div>
    </div>
  );
}

/* ═══════════════════  TIME FILTER  ═══════════════════ */
function TimeFilter({ value, onChange }: { value: "today" | "7d" | "30d"; onChange: (v: "today" | "7d" | "30d") => void }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-1 inline-flex">
      {(["today", "7d", "30d"] as const).map((k) => (
        <button key={k} onClick={() => onChange(k)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${value === k ? "bg-teal-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"}`}>
          {k === "today" ? "今日" : k === "7d" ? "近7天" : "近30天"}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════  CHARTS  ═══════════════════ */
function BarRankChart({ data, title }: { data: { name: string; value: number }[]; title: string }) {
  const max = Math.max(...data.map((d) => d.value));
  const colors = ["bg-pink-500", "bg-amber-500", "bg-violet-500", "bg-gray-500", "bg-orange-500", "bg-green-500"];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map((item, i) => {
          const pct = (item.value / max) * 100;
          return (
            <div key={item.name} className="flex items-center gap-3">
              <span className="text-xs text-gray-500 w-16 text-right whitespace-nowrap">{item.name}</span>
              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${colors[i % colors.length]} transition-all duration-700 ease-out`} style={{ width: `${pct}%`, transitionDelay: `${i * 100}ms` }} />
              </div>
              <span className="text-xs font-medium text-gray-700 w-12 text-right tabular-nums">{item.value.toLocaleString()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SceneHeatChart({ data }: { data: { name: string; value: number }[] }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const bgColors = ["bg-emerald-500", "bg-sky-500", "bg-amber-500", "bg-rose-500"];
  const colorMap: Record<string, string> = {
    "山水风景": "#10b981",
    "城市风光": "#0ea5e9",
    "日出日落": "#f59e0b",
    "名胜古迹": "#f43f5e",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">场景热度分布</h3>
      <div className="flex items-center gap-4">
        <div className="w-32 h-32 relative">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            {data.reduce((acc, item, i) => {
              const prev = acc.prev;
              const pct = (item.value / total) * 100;
              const circumference = 2 * Math.PI * 40;
              const dashArray = `${(pct / 100) * circumference} ${circumference}`;
              const dashOffset = -((prev / 100) * circumference);
              acc.elements.push(
                <circle key={item.name} cx="50" cy="50" r="40" fill="none" stroke={colorMap[item.name] || "#9ca3af"} strokeWidth="12" strokeDasharray={dashArray} strokeDashoffset={dashOffset} strokeLinecap="round" className="transition-all duration-1000" style={{ transitionDelay: `${i * 150}ms` }} />
              );
              acc.prev += pct;
              return acc;
            }, { elements: [] as React.ReactNode[], prev: 0 }).elements}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center flex-col">
            <div className="text-lg font-bold text-gray-900">{total.toLocaleString()}</div>
            <div className="text-[10px] text-gray-500">总生成</div>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          {data.map((item, i) => (
            <div key={item.name} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${bgColors[i % bgColors.length]}`} />
              <span className="text-xs text-gray-600 flex-1">{item.name}</span>
              <span className="text-xs font-medium text-gray-900">{item.value.toLocaleString()}</span>
              <span className="text-xs text-gray-400">({Math.round((item.value / total) * 100)}%)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DailyTrendChart({ data, label }: { data: { hour: string; value: number }[]; label: string }) {
  const max = Math.max(...data.map((d) => d.value));
  const chartWidth = 100;
  const chartHeight = 100;
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * chartWidth;
    const y = chartHeight - (d.value / max) * chartHeight * 0.8 - 10;
    return `${x},${y}`;
  }).join(" ");
  const areaPoints = `0,${chartHeight} ${points} ${chartWidth},${chartHeight}`;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">生成趋势</h3>
        <span className="text-xs text-gray-400">{label}</span>
      </div>
      <div className="relative h-40">
        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={areaPoints} fill="url(#trendGradient)" />
          <polyline points={points} fill="none" stroke="#14b8a6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          {data.map((d, i) => {
            const x = (i / (data.length - 1)) * chartWidth;
            const y = chartHeight - (d.value / max) * chartHeight * 0.8 - 10;
            return <circle key={i} cx={x} cy={y} r="1.5" fill="#14b8a6" />;
          })}
        </svg>
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-gray-400">
          {data.length <= 8 ? (
            data.map((d, i) => <span key={i}>{d.hour}</span>)
          ) : (
            <>
              <span>{data[0].hour}</span>
              <span>{data[Math.floor(data.length / 4)].hour}</span>
              <span>{data[Math.floor(data.length / 2)].hour}</span>
              <span>{data[Math.floor(data.length * 3 / 4)].hour}</span>
              <span>{data[data.length - 1].hour}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════  STYLE COLLECTION CARD  ═══════════════════ */
function StyleCollectionCard({ collection, isSelected, onClick, delay }: {
  collection: StyleCollection; isSelected: boolean; onClick: () => void; delay: number;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);

  return (
    <div onClick={onClick} className={`relative rounded-xl overflow-hidden cursor-pointer border-2 transition-all duration-500 hover:shadow-lg hover:-translate-y-1 ${isSelected ? "border-teal-500 shadow-lg scale-[1.02]" : "border-transparent"} ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="relative h-40 w-full bg-gradient-to-b from-slate-50 to-slate-200">
        <img src={collection.coverImage} alt={collection.name} className={`w-full h-full transition-transform duration-500 hover:scale-105 ${collection.name === "AI旅拍" ? "object-cover" : "object-contain p-2"}`} />
        {isSelected && (
          <div className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-teal-500 rounded-full text-white">
            <i className="ri-check-line" />
          </div>
        )}
      </div>
      <div className="p-3 bg-white border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{collection.name}</span>
          <span className="px-2 py-0.5 rounded-full bg-teal-50 text-xs font-medium text-teal-700">{collection.templateCount} 个模板</span>
          <span className="ml-auto text-xs text-gray-400">{collection.generateCount.toLocaleString()} 次生成</span>
        </div>
        <p className="text-xs text-gray-400 mt-1 line-clamp-1">{collection.description}</p>
      </div>
    </div>
  );
}

/* ═══════════════════  STYLE TEMPLATE CARD  ═══════════════════ */
function StyleTemplateCard({ template, onPreview, onEdit, onDelete, onPreviewEffect, onToggleEnabled, delay, batchMode, selected, onToggleSelect }: {
  template: ManagedStyleTemplate; onPreview: (t: ManagedStyleTemplate) => void; onEdit: (t: ManagedStyleTemplate) => void; onDelete: (t: ManagedStyleTemplate) => void; onPreviewEffect: (t: ManagedStyleTemplate) => void; delay: number;
  onToggleEnabled: (t: ManagedStyleTemplate) => void;
  batchMode: boolean; selected: boolean; onToggleSelect: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);

  const isWan = templateGenerationProvider(template) === "wan";
  const providerLabel = isWan ? "万相" : "FaceFusion";
  const styleBadgeClass = getStyleColor(template.styleId);
  const styleText = template.styleName || getStyleLabel(template.styleId);
  const showCategoryBadge = Boolean(
    styleText &&
    styleText !== "万相" &&
    styleText !== "wan" &&
    styleText !== providerLabel
  );
  const statusBadgeClass = template.complianceStatus === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";
  const statusText = template.complianceStatus === "approved" ? "已通过" : "待审核";
  return (
    <div className={`group h-full flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${selected ? "ring-2 ring-teal-500" : ""}`}>
      <div className="relative w-full h-56 shrink-0 overflow-hidden cursor-pointer bg-gradient-to-b from-slate-50 to-slate-200" onClick={() => batchMode ? onToggleSelect(template.id) : onPreview(template)}>
        {/* IP cutouts (transparent) letterbox on a light backdrop; scenery
            photos (AI旅拍) fill the frame edge to edge. */}
        <img src={template.image} alt={template.name} loading="lazy" decoding="async" className={`w-full h-full transition-transform duration-500 group-hover:scale-105 ${(template.styleName === "AI旅拍" || template.style === "photo") ? "object-cover" : "object-contain p-2"}`} />
        {batchMode && (
          <div className="absolute top-3 left-3 z-10 pointer-events-auto" onClick={(e) => { e.stopPropagation(); onToggleSelect(template.id); }}>
            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${selected ? "bg-teal-600 border-teal-600" : "bg-white/80 border-white/80 hover:bg-white"}`}>
              {selected && <i className="ri-check-line text-white text-xs" />}
            </div>
          </div>
        )}
        {/* status only as a quiet corner chip; the figure stays unobstructed */}
        <span className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-medium pointer-events-none ${statusBadgeClass}`}>{statusText}</span>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-300 flex items-end justify-center pb-3 gap-2 pointer-events-none">
          <button onClick={(e) => { e.stopPropagation(); onPreview(template); }} aria-label="预览模板" className="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full bg-white/95 text-xs font-medium text-gray-800 flex items-center gap-1 hover:bg-white">
            <i className="ri-eye-line" /> 预览
          </button>
          <button onClick={(e) => { e.stopPropagation(); onPreviewEffect(template); }} aria-label="查看合成效果" className="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full bg-teal-600 text-xs font-medium text-white flex items-center gap-1 hover:bg-teal-700">
            <i className="ri-image-edit-line" /> 合成效果
          </button>
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-between px-4 py-2.5 border-t border-gray-100">
        <div className="space-y-2">
          {/* Row 1: Dedicated template name & usage count */}
          <div className="flex items-center justify-between gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 min-w-0 flex-1 truncate" title={template.name}>
              {template.name}
            </h3>
            <span className="text-xs text-gray-400 shrink-0 tabular-nums">
              {template.generateCount.toLocaleString()} 次生成
            </span>
          </div>

          {/* Row 2: Metadata badges (wrapping, with redundant Wan category suppressed) */}
          <div className="flex flex-wrap items-center content-start gap-1.5 min-h-[22px]">
            {showCategoryBadge && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${styleBadgeClass}`}>{styleText}</span>
            )}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${isWan ? "bg-violet-50 text-violet-700 border border-violet-200" : "bg-gray-50 text-gray-600 border border-gray-200"}`}>
              {providerLabel}
            </span>
            {template.style && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${
                template.style === "photo" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                template.style === "chibi" ? "bg-pink-50 text-pink-700 border border-pink-200" :
                "bg-amber-50 text-amber-700 border border-amber-200"
              }`}>
                {FACESWAP_STYLE_LABELS[template.style] || template.style}
              </span>
            )}
            {isWan && template.outfitType && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 bg-purple-50 text-purple-700 border border-purple-200 truncate max-w-[90px]" title={template.outfitType.name}>
                {template.outfitType.name}
              </span>
            )}
            {isWan && (() => {
              const coverage = getTemplateBodyCoverageTag(template);
              const coverageName = coverage?.name || "全身";
              const isUnavailable = coverage?.enabled === false;
              return (
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${
                    isUnavailable
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-indigo-50 text-indigo-700 border border-indigo-200"
                  }`}
                  title={isUnavailable ? `服装范围（不可用）：${coverageName}` : `服装范围：${coverageName}`}
                >
                  {coverageName}
                </span>
              );
            })()}
            {isWan && (template.supplementalImages?.length || 0) > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 bg-teal-50 text-teal-700 border border-teal-200">
                {template.supplementalImages!.length} 张补充图
              </span>
            )}
            {isWan && (
              (template.actionPrompts?.length || 0) > 0 ? (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 bg-emerald-50 text-emerald-700 border border-emerald-200"
                  title={`适用动作白名单：${template.actionPrompts!.map((p) => p.name + (!p.enabled ? " (已停用)" : "")).join("、")}`}
                >
                  {template.actionPrompts!.length} 个动作
                </span>
              ) : (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 bg-gray-50 text-gray-500 border border-gray-200"
                  title="未配置动作白名单，生成将按「模板提示词 + 人物年龄性别提示词 + 固定提示词（人体约束）」拼接（不追加动作）"
                >
                  无动作白名单
                </span>
              )
            )}
          </div>
        </div>

        {/* Row 3: Bottom toggle switch and action buttons */}
        <div className="flex items-center justify-between gap-1.5 pt-2 mt-2 border-t border-gray-50">
          <label className="relative inline-flex items-center cursor-pointer mr-auto" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={template.enabled} className="sr-only peer" onChange={(e) => { e.stopPropagation(); onToggleEnabled(template); }} />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600" />
          </label>
          <div className="flex items-center gap-1">
            <button onClick={(e) => { e.stopPropagation(); onEdit(template); }} aria-label="编辑模板" className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
              <i className="ri-edit-line text-sm" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(template); }} aria-label="删除模板" className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════  PURE SWAP TEMPLATE CARD  ═══════════════════ */
function PureSwapTemplateCard({ template, onPreview, onUpload, onEdit, onPreviewEffect, onToggleEnabled, delay, batchMode, selected, onToggleSelect }: {
  template: PureSwapTemplate; onPreview: (t: PureSwapTemplate) => void; onUpload: (t: PureSwapTemplate) => void; onEdit: (t: PureSwapTemplate) => void; onPreviewEffect: (t: PureSwapTemplate) => void; delay: number;
  onToggleEnabled: (t: PureSwapTemplate) => void;
  batchMode: boolean; selected: boolean; onToggleSelect: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);

  const sceneBadgeClass = getSceneTypeColor(template.sceneType);
  const sceneText = getSceneTypeLabel(template.sceneType);
  const statusBadgeClass = template.complianceStatus === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";
  const statusText = template.complianceStatus === "approved" ? "已通过" : "待审核";

  return (
    <div className={`group bg-white rounded-xl border border-gray-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${selected ? "ring-2 ring-teal-500" : ""}`}>
      <div className="relative w-full h-64 overflow-hidden">
        <img src={template.image} alt={template.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
        {batchMode && (
          <div className="absolute top-3 left-3 z-10 pointer-events-auto" onClick={(e) => { e.stopPropagation(); onToggleSelect(template.id); }}>
            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${selected ? "bg-teal-600 border-teal-600" : "bg-white/80 border-white/80 hover:bg-white"}`}>
              {selected && <i className="ri-check-line text-white text-xs" />}
            </div>
          </div>
        )}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 pointer-events-none">
          <div className="absolute inset-0 rounded-full border-2 border-white/60 border-dashed animate-[spin_8s_linear_infinite]" />
          <div className="absolute inset-2 rounded-full border border-white/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] text-white/80 font-medium bg-black/30 px-1.5 py-0.5 rounded">人脸区域</span>
          </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex flex-col items-center justify-center gap-2 pointer-events-none">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-2 pointer-events-auto">
            <button onClick={(e) => { e.stopPropagation(); onPreview(template); }} aria-label="预览场景" className="px-3 py-1.5 rounded-full bg-white/90 text-sm font-medium text-gray-800 flex items-center gap-1.5 hover:bg-white transition-colors">
              <i className="ri-eye-line" /> 预览
            </button>
            <button onClick={(e) => { e.stopPropagation(); onPreviewEffect(template); }} aria-label="查看合成效果" className="px-3 py-1.5 rounded-full bg-amber-500 text-sm font-medium text-white flex items-center gap-1.5 hover:bg-amber-600 transition-colors">
              <i className="ri-image-edit-line" /> 合成效果
            </button>
            <button onClick={(e) => { e.stopPropagation(); onUpload(template); }} aria-label="模拟换脸" className="px-3 py-1.5 rounded-full bg-teal-600 text-sm font-medium text-white flex items-center gap-1.5 hover:bg-teal-700 transition-colors">
              <i className="ri-upload-2-line" /> 模拟换脸
            </button>
          </div>
        </div>
        <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${sceneBadgeClass}`}>{sceneText}</span>
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusBadgeClass}`}>{statusText}</span>
          </div>
          <h3 className="text-white font-medium text-sm">{template.name}</h3>
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs text-gray-500 mb-3">{template.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{template.generateCount.toLocaleString()} 次生成</span>
          <div className="flex items-center gap-2">
            <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={template.enabled} className="sr-only peer" onChange={(e) => { e.stopPropagation(); onToggleEnabled(template); }} />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600" />
            </label>
            <button onClick={(e) => { e.stopPropagation(); onEdit(template); }} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
              <i className="ri-edit-line" />
            </button>
            <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
              <i className="ri-delete-bin-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════  DETAIL DRAWERS  ═══════════════════ */
function StyleDetailDrawer({ template, onClose, onEdit, onDelete, onToggleEnabled }: { template: ManagedStyleTemplate | null; onClose: () => void; onEdit: (t: ManagedStyleTemplate) => void; onDelete: (t: ManagedStyleTemplate) => void; onToggleEnabled: (t: ManagedStyleTemplate) => void }) {
  if (!template) return null;
  const style = mockStyleCollections.find((s) => s.id === template.styleId);
  const styleBadgeClass = getStyleColor(template.styleId);
  const statusBadgeClass = template.complianceStatus === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto animate-[slideIn_0.3s_ease-out]">
        <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        <div className="relative h-80 overflow-hidden">
          <img src={template.image} alt={template.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
          <button onClick={onClose} aria-label="关闭详情" className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full hover:bg-white transition-colors pointer-events-auto">
            <i className="ri-close-line text-gray-700" />
          </button>
          <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${styleBadgeClass}`}>{template.styleName}</span>
              {template.style && (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  template.style === "photo" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                  template.style === "chibi" ? "bg-pink-50 text-pink-700 border border-pink-200" :
                  "bg-amber-50 text-amber-700 border border-amber-200"
                }`}>
                  {FACESWAP_STYLE_LABELS[template.style] || template.style}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass}`}>{template.complianceStatus === "approved" ? "已通过" : "待审核"}</span>
            </div>
            <h2 className="text-xl font-bold text-white">{template.name}</h2>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">模板描述</h3>
            <p className="text-sm text-gray-600">{template.description}</p>
          </div>
          <div>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">模板提示词 (Prompt)</h3>
              {templateGenerationProvider(template) === "wan" && (
                <span className="text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">
                  仅展示模板提示词 · 生成时系统自动拼接年龄性别/动作/固定提示词
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-100">{template.prompt || "未设置提示词"}</p>
          </div>
          {templateGenerationProvider(template) === "wan" && (
            <div className="border border-violet-200 bg-violet-50/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-violet-900 flex items-center gap-1.5">
                  <i className="ri-magic-line text-violet-600" />
                  万相模型输入契约 (Wan Audit)
                </h3>
                <div className="flex items-center gap-1.5">
                  <span className="px-2 py-0.5 rounded bg-violet-100 text-violet-700 text-xs font-medium">
                    {template.outfitType ? `${template.outfitType.name} (${template.outfitType.code})` : "未指定服装大类"}
                  </span>
                  {(() => {
                    const coverage = getTemplateBodyCoverageTag(template);
                    const coverageName = coverage?.name || "全身";
                    const isUnavailable = coverage?.enabled === false;
                    return (
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          isUnavailable
                            ? "bg-red-100 text-red-700"
                            : "bg-indigo-100 text-indigo-700"
                        }`}
                        title={isUnavailable ? `服装范围（不可用）：${coverageName}` : `服装范围：${coverageName}`}
                      >
                        {coverageName}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-white border border-violet-100 rounded p-2 flex flex-col items-center text-center">
                  <span className="font-bold text-violet-700 mb-1">图1 底图</span>
                  <div className="w-10 h-10 rounded overflow-hidden border border-gray-200 mb-0.5">
                    <img src={template.image} alt="底图" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="bg-white border border-violet-100 rounded p-2 flex flex-col items-center text-center justify-center">
                  <span className="font-bold text-violet-700 mb-0.5">图2 运行时人像</span>
                  <span className="text-[10px] text-gray-500">用户自拍人像</span>
                </div>
                <div className="bg-white border border-violet-100 rounded p-2 flex flex-col items-center text-center justify-center">
                  <span className="font-bold text-violet-700 mb-0.5">图3 匹配服装</span>
                  <span className="text-[10px] text-gray-500">
                    {template.outfitType?.name
                      ? `${template.outfitType.name} · ${getTemplateBodyCoverageTag(template)?.name || "全身"}`
                      : `大类+范围选定 (${getTemplateBodyCoverageTag(template)?.name || "全身"})`}
                  </span>
                </div>
                <div className="bg-white border border-violet-100 rounded p-2 flex flex-col items-center text-center justify-center">
                  <span className="font-bold text-violet-700 mb-0.5">图4+ 补充参考图</span>
                  <span className="text-[10px] text-gray-500">
                    {(template.supplementalImages?.length || 0) > 0 ? `${template.supplementalImages!.length} 张参考图` : "无补充图"}
                  </span>
                </div>
              </div>
              {template.supplementalImages && template.supplementalImages.length > 0 && (
                <div className="pt-1">
                  <div className="text-xs font-medium text-violet-900 mb-1">补充参考图列表：</div>
                  <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    {template.supplementalImages.map((img, idx) => (
                      <div key={img.id || idx} className="relative shrink-0 text-center">
                        <span className="absolute top-0.5 left-0.5 px-1 bg-black/60 text-white text-[9px] rounded font-semibold z-10">
                          图{idx + 4}
                        </span>
                        <img
                          src={img.imageUrl}
                          alt={`图${idx + 4}`}
                          className="w-14 h-14 object-cover rounded border border-gray-200"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="pt-2 border-t border-violet-200/60">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-violet-900 flex items-center gap-1">
                    <i className="ri-walk-line text-violet-600" />
                    适用动作提示词白名单
                  </span>
                  <span className="text-[11px] text-violet-700 font-medium">
                    {(template.actionPrompts?.length || 0) > 0
                      ? `已配置 ${template.actionPrompts!.length} 个动作`
                      : "未配置白名单（按模板提示词 + 人物年龄性别提示词 + 固定提示词生成）"}
                  </span>
                </div>
                {(template.actionPrompts?.length || 0) > 0 ? (
                  <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                    {template.actionPrompts!.map((p) => (
                      <div
                        key={p.id}
                        className="p-1.5 bg-white rounded border border-violet-100 text-xs flex items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-medium text-gray-800 truncate">{p.name}</span>
                          {!p.enabled && (
                            <span className="px-1.5 py-0.2 rounded text-[10px] bg-red-100 text-red-700 shrink-0">
                              已停用
                            </span>
                          )}
                        </div>
                        {p.text && (
                          <span className="text-[11px] text-gray-400 truncate max-w-[200px]" title={p.text}>
                            {p.text}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-500 bg-white/70 p-2 rounded border border-violet-100">
                    该模板未配置适用动作白名单。万相生成时将不追加任何动作描述，按「模板提示词 + 人物年龄性别提示词 + 固定提示词（人体约束）」正常生成。
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{template.generateCount.toLocaleString()}</div>
              <div className="text-xs text-gray-500">累计生成</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{template.todayCount}</div>
              <div className="text-xs text-gray-500">今日生成</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{style?.templateCount || 0}</div>
              <div className="text-xs text-gray-500">同风格模板</div>
            </div>
          </div>
          {style && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">风格预览</h3>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden">
                  <img src={style.coverImage} alt={style.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">{style.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{style.description}</div>
                  <div className="flex gap-1 mt-1.5">
                    {style.tags.map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-gray-500">{t}</span>)}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">启用状态</span>
              <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={template.enabled} className="sr-only peer" onChange={(e) => { e.stopPropagation(); onToggleEnabled(template); }} />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => onEdit(template)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
                <i className="ri-edit-line mr-1" />编辑
              </button>
              <button onClick={() => onDelete(template)} className="px-4 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap">
                <i className="ri-delete-bin-line mr-1" />删除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PureDetailDrawer({ template, onClose, onEdit, onToggleEnabled }: { template: PureSwapTemplate | null; onClose: () => void; onEdit: (t: PureSwapTemplate) => void; onToggleEnabled: (t: PureSwapTemplate) => void }) {
  if (!template) return null;
  const sceneBadgeClass = getSceneTypeColor(template.sceneType);
  const statusBadgeClass = template.complianceStatus === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";
  const facePosText = template.facePosition === "center" ? "居中" : template.facePosition === "left" ? "左侧" : "右侧";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto animate-[slideIn_0.3s_ease-out]">
        <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        <div className="relative h-80 overflow-hidden">
          <img src={template.image} alt={template.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
          <button onClick={onClose} aria-label="关闭详情" className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full hover:bg-white transition-colors pointer-events-auto">
            <i className="ri-close-line text-gray-700" />
          </button>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 pointer-events-none">
            <div className="absolute inset-0 rounded-full border-2 border-white/70 border-dashed animate-[spin_8s_linear_infinite]" />
            <div className="absolute inset-2 rounded-full border border-white/50" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs text-white font-medium bg-black/40 px-2 py-1 rounded">人脸替换区域</span>
            </div>
          </div>
          <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${sceneBadgeClass}`}>{getSceneTypeLabel(template.sceneType)}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass}`}>{template.complianceStatus === "approved" ? "已通过" : "待审核"}</span>
            </div>
            <h2 className="text-xl font-bold text-white">{template.name}</h2>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">场景描述</h3>
            <p className="text-sm text-gray-600">{template.description}</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{template.generateCount.toLocaleString()}</div>
              <div className="text-xs text-gray-500">累计生成</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{template.todayCount}</div>
              <div className="text-xs text-gray-500">今日生成</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{facePosText}</div>
              <div className="text-xs text-gray-500">人脸位置</div>
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">换脸效果预览</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative rounded-lg overflow-hidden">
                <img src={template.image} alt="原图" className="w-full h-32 object-cover" />
                <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-0.5 rounded text-xs text-white">原模板</div>
              </div>
              <div className="relative rounded-lg overflow-hidden bg-gray-100">
                <img src={template.image} alt="换脸后" className="w-full h-32 object-cover opacity-60" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 border-teal-500 border-dashed flex items-center justify-center bg-white/80">
                  <i className="ri-user-smile-line text-teal-600 text-lg" />
                </div>
                <div className="absolute bottom-2 left-2 bg-teal-600/80 px-2 py-0.5 rounded text-xs text-white">换脸效果</div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">游客上传自拍照后，AI 将自动替换画面中的人脸区域</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">启用状态</span>
              <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={template.enabled} className="sr-only peer" onChange={(e) => { e.stopPropagation(); onToggleEnabled(template); }} />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => onEdit(template)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
                <i className="ri-edit-line mr-1" />编辑
              </button>
              <button className="px-4 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap">
                <i className="ri-delete-bin-line mr-1" />删除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════  CREATE MODALS  ═══════════════════ */
function CreateStyleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState({ name: "", style: "", description: "", tags: "" });
  const [submitted, setSubmitted] = useState(false);
  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setForm({ name: "", style: "", description: "", tags: "" }); onClose(); }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-lg mx-4 overflow-hidden animate-[popIn_0.25s_ease-out]">
        <style>{`@keyframes popIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">新建风格</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><i className="ri-close-line" /></button>
        </div>
        {submitted ? (
          <div className="p-10 flex flex-col items-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full"><i className="ri-check-line text-emerald-600 text-2xl" /></div>
            <p className="text-sm font-medium text-gray-900">风格创建成功</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">风格名称</label>
              <input type="text" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="例如：赛博朋克" className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">风格标识</label>
              <input type="text" required value={form.style} onChange={(e) => setForm((f) => ({ ...f, style: e.target.value }))} placeholder="例如：cyberpunk" className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
              <p className="text-xs text-gray-400 mt-1">用于系统内部标识，建议使用英文</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">风格描述</label>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="简要描述该风格的特点" rows={3} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">标签</label>
              <input type="text" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="用逗号分隔，例如：科幻, 未来, 霓虹" className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
              <button type="submit" className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">创建风格</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CreateTemplateModal({ open, onClose, defaultCategory, onCreated, outfitTags, outfitTagGroups, actionPrompts }: {
  open: boolean; onClose: () => void; defaultCategory?: string | null; onCreated?: () => void;
  outfitTags: OutfitTypeTag[]; outfitTagGroups?: OutfitTagGroup[];
  actionPrompts: ActionPrompt[];
}) {
  const { topCategories, subMap, sub2Map } = useCategoryTree();
  const initialCat = defaultCategory || topCategories[0] || "";
  const initialSub = (subMap[initialCat] || [])[0] || "";
  const [form, setForm] = useState({ name: "", category: initialCat, subcategory: initialSub, subsubcategory: "", style: defaultFaceswapStyle(initialCat), generationProvider: "facefusion" as GenerationProvider, prompt: "" });
  const [outfitTypeTagId, setOutfitTypeTagId] = useState("");
  const [bodyCoverageTagId, setBodyCoverageTagId] = useState("");
  const [supplementalItems, setSupplementalItems] = useState<LocalSupplementalItem[]>([]);
  const [actionPromptIds, setActionPromptIds] = useState<string[]>([]);
  const [styleTouched, setStyleTouched] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const c = defaultCategory || topCategories[0] || "";
      const s = (subMap[c] || [])[0] || "";
      setForm({ name: "", category: c, subcategory: s, subsubcategory: "", style: defaultFaceswapStyle(c), generationProvider: "facefusion", prompt: "" });
      setOutfitTypeTagId("");
      const defaultCoverage = getDefaultFullBodyTag(outfitTags, outfitTagGroups);
      setBodyCoverageTagId(defaultCoverage?.id || "");
      setSupplementalItems([]);
      setActionPromptIds([]);
      setStyleTouched(false);
      setFile(null);
      setPreviewUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCategory]);
  useEffect(() => {
    if (!bodyCoverageTagId) {
      const defaultCoverage = getDefaultFullBodyTag(outfitTags, outfitTagGroups);
      if (defaultCoverage) {
        setBodyCoverageTagId(defaultCoverage.id);
      }
    }
  }, [bodyCoverageTagId, outfitTags, outfitTagGroups]);


  if (!open) return null;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setPreviewUrl(URL.createObjectURL(f)); }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { toast("请上传模板图片", "error"); return; }
    if (!form.name.trim()) { toast("请填写模板名称", "error"); return; }
    if (form.generationProvider === "wan") {
      if (form.category !== "万相" || form.style !== "photo") {
        toast("万相模板必须归入“万相”一级分类且风格为纯换脸(photo)", "error");
        return;
      }
      if (!outfitTypeTagId) {
        toast("万相模板必须选择服装大类", "error");
        return;
      }
      if (!isAvailableOutfitTypeTag(outfitTypeTagId, outfitTags, outfitTagGroups)) {
        toast("当前关联的服装大类不可用，请重新选择后再保存", "error");
        return;
      }
      if (!bodyCoverageTagId) {
        toast("万相模板必须选择服装范围", "error");
        return;
      }
      if (!isAvailableBodyCoverageTag(bodyCoverageTagId, outfitTags, outfitTagGroups)) {
        toast("当前关联的服装范围不可用，请重新选择后再保存", "error");
        return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("category", form.category);
      fd.append("subcategory", form.subcategory);
      fd.append("subsubcategory", form.subsubcategory);
      fd.append("style", form.style);
      fd.append("generation_provider", form.generationProvider);
      fd.append("scene", "03");
      fd.append("prompt", form.prompt.trim());
      fd.append("file", file);

      if (form.generationProvider === "wan") {
        fd.append("outfit_type_tag_id", outfitTypeTagId);
        fd.append("outfit_filter_tag_ids", JSON.stringify([bodyCoverageTagId]));
        fd.append("action_prompt_ids", JSON.stringify(actionPromptIds));
        const manifest: { fileIndex: number }[] = [];
        const supplementalFiles: File[] = [];
        supplementalItems.forEach((item) => {
          if (item.kind === "new" && item.file) {
            manifest.push({ fileIndex: supplementalFiles.length });
            supplementalFiles.push(item.file);
          }
        });
        fd.append("supplemental_manifest", JSON.stringify(manifest));
        supplementalFiles.forEach((f) => fd.append("supplemental_files", f));
      } else {
        fd.append("outfit_filter_tag_ids", JSON.stringify([]));
        fd.append("action_prompt_ids", JSON.stringify([]));
      }

      const res = await apiFetch("/faceswap/templates", { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      toast("模板创建成功", "success");
      onCreated?.();
      onClose();
    } catch (err) {
      toast("创建失败：" + (err instanceof Error ? err.message : String(err)), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-lg mx-4 overflow-hidden animate-[popIn_0.25s_ease-out] max-h-[90vh] overflow-y-auto">
        <style>{`@keyframes popIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">新建模板</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><i className="ri-close-line" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">模板图片</label>
            <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-gray-200 rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 transition-colors">
              {previewUrl ? (
                <img src={previewUrl} alt="预览" className="w-full h-40 object-cover rounded-lg" />
              ) : (
                <><i className="ri-image-add-line text-2xl text-gray-400" /><span className="text-sm text-gray-500">点击上传模板图片</span><span className="text-xs text-gray-400">JPG / PNG，建议 400×500 像素</span></>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">模板名称</label>
            <input type="text" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="例如：船长 - 正装合影" className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">一级分类</label>
              <select required value={form.category} onChange={(e) => { const c = e.target.value; setForm((f) => ({ ...f, category: c, subcategory: (subMap[c] || [])[0] || "", subsubcategory: "", style: styleTouched ? f.style : defaultFaceswapStyle(c) })); }} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                {topCategories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">二级分类</label>
              <select required value={form.subcategory} onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value, subsubcategory: "" }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                {(subMap[form.category] || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {(() => {
            const opts = sub2Map[sub2Key(form.category, form.subcategory)] || [];
            const empty = opts.length === 0;
            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">三级分类 <span className="text-gray-400 text-xs">(可选{empty ? "，当前二级下暂未细分" : ""})</span></label>
                <select disabled={empty} value={form.subsubcategory} onChange={(e) => setForm((f) => ({ ...f, subsubcategory: e.target.value }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 disabled:opacity-60">
                  <option value="">{empty ? "— 无三级 —" : "— 不选 —"}</option>
                  {opts.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            );
          })()}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">生成风格</label>
            <select required value={form.style} onChange={(e) => { setStyleTouched(true); setForm((f) => ({ ...f, style: e.target.value as FaceSwapStyle })); }} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
              {FACESWAP_STYLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.description})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">生成管线</label>
            <select
              required
              value={form.generationProvider}
              onChange={(e) => {
                const provider = e.target.value as GenerationProvider;
                if (provider === "wan") {
                  const subs = subMap["万相"] || [];
                  setForm((f) => ({
                    ...f,
                    generationProvider: "wan",
                    category: "万相",
                    subcategory: subs.includes(f.subcategory) ? f.subcategory : (subs[0] || "其他"),
                    subsubcategory: "",
                    style: "photo",
                  }));
                  if (!bodyCoverageTagId) {
                    const defaultCoverage = getDefaultFullBodyTag(outfitTags, outfitTagGroups);
                    if (defaultCoverage) setBodyCoverageTagId(defaultCoverage.id);
                  }
                } else {
                  setForm((f) => ({ ...f, generationProvider: "facefusion" }));
                }
              }}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              {GENERATION_PROVIDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}（{option.description}）</option>
              ))}
            </select>
            {form.style === "photo" && form.generationProvider === "facefusion" && <p className="mt-1 text-xs text-amber-600">旧 photo 模板请选择 FaceFusion；万相 photo 模板请选择万相。</p>}
          </div>
          {form.generationProvider === "wan" && (
            <WanTemplateGenerationFields
              outfitTypeTagId={outfitTypeTagId}
              onOutfitTypeTagIdChange={setOutfitTypeTagId}
              bodyCoverageTagId={bodyCoverageTagId}
              onBodyCoverageTagIdChange={setBodyCoverageTagId}
              outfitTags={outfitTags}
              outfitTagGroups={outfitTagGroups}
              supplementalItems={supplementalItems}
              onSupplementalItemsChange={setSupplementalItems}
              actionPromptIds={actionPromptIds}
              onActionPromptIdsChange={setActionPromptIds}
              allActionPrompts={actionPrompts}
              templateActionPrompts={[]}
              disabled={submitting}
            />
          )}
          <div>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">模板提示词 (Prompt)</label>
              {form.generationProvider === "wan" && (
                <span className="text-[11px] text-teal-600 font-normal">
                  仅需填写场景与构图描述，年龄性别、动作及固定提示词由系统自动追加
                </span>
              )}
            </div>
            <textarea
              value={form.prompt}
              onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
              placeholder={
                form.generationProvider === "wan"
                  ? "请输入模板提示词（专注场景构图、机位光影与服装融合；无需写人物年龄/性别、动作姿态及固定人体结构约束）..."
                  : "请输入提示词..."
              }
              rows={3}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
            />
            <WanPromptGuidance generationProvider={form.generationProvider} style={form.style} />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
            <button type="submit" disabled={submitting} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-60">{submitting ? "创建中..." : "创建模板"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════  BATCH UPLOAD MODAL  ═══════════════════ */
function BatchUploadModal({ open, onClose, defaultCategory, onUploaded, outfitTags, outfitTagGroups, actionPrompts }: {
  open: boolean; onClose: () => void; defaultCategory?: string | null; onUploaded?: () => void;
  outfitTags: OutfitTypeTag[]; outfitTagGroups?: OutfitTagGroup[];
  actionPrompts: ActionPrompt[];
}) {
  const { topCategories, subMap, sub2Map } = useCategoryTree();
  const initialCat = defaultCategory || topCategories[0] || "";
  const [category, setCategory] = useState(initialCat);
  const [subcategory, setSubcategory] = useState((subMap[initialCat] || [])[0] || "");
  const [subsubcategory, setSubsubcategory] = useState("");
  const [style, setStyle] = useState<FaceSwapStyle>(defaultFaceswapStyle(initialCat));
  const [generationProvider, setGenerationProvider] = useState<GenerationProvider>("facefusion");
  const [outfitTypeTagId, setOutfitTypeTagId] = useState("");
  const [bodyCoverageTagId, setBodyCoverageTagId] = useState("");
  const [actionPromptIds, setActionPromptIds] = useState<string[]>([]);
  const [prompt, setPrompt] = useState("");
  const [styleTouched, setStyleTouched] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const c = defaultCategory || topCategories[0] || "";
      setCategory(c);
      setSubcategory((subMap[c] || [])[0] || "");
      setSubsubcategory("");
      setStyle(defaultFaceswapStyle(c));
      setGenerationProvider("facefusion");
      setOutfitTypeTagId("");
      const defaultCoverage = getDefaultFullBodyTag(outfitTags, outfitTagGroups);
      setBodyCoverageTagId(defaultCoverage?.id || "");
      setPrompt("");
      setStyleTouched(false);
      setFiles([]);
      setActionPromptIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCategory]);
  useEffect(() => {
    if (!bodyCoverageTagId) {
      const defaultCoverage = getDefaultFullBodyTag(outfitTags, outfitTagGroups);
      if (defaultCoverage) {
        setBodyCoverageTagId(defaultCoverage.id);
      }
    }
  }, [bodyCoverageTagId, outfitTags, outfitTagGroups]);


  if (!open) return null;

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...fl]);
  };
  const removeFile = (i: number) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) { toast("请选择至少一张图片", "error"); return; }
    if (generationProvider === "wan") {
      if (category !== "万相" || style !== "photo") {
        toast("万相模板必须归入“万相”一级分类且风格为纯换脸(photo)", "error");
        return;
      }
      if (!outfitTypeTagId) {
        toast("万相模板必须选择服装大类", "error");
        return;
      }
      if (!isAvailableOutfitTypeTag(outfitTypeTagId, outfitTags, outfitTagGroups)) {
        toast("当前选择的服装大类不可用，请重新选择后再上传", "error");
        return;
      }
      if (!bodyCoverageTagId) {
        toast("万相模板必须选择服装范围", "error");
        return;
      }
      if (!isAvailableBodyCoverageTag(bodyCoverageTagId, outfitTags, outfitTagGroups)) {
        toast("当前选择的服装范围不可用，请重新选择后再上传", "error");
        return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("subcategory", subcategory);
      fd.append("subsubcategory", subsubcategory);
      fd.append("style", style);
      fd.append("generation_provider", generationProvider);
      if (generationProvider === "wan") {
        fd.append("outfit_type_tag_id", outfitTypeTagId);
        fd.append("outfit_filter_tag_ids", JSON.stringify([bodyCoverageTagId]));
        fd.append("action_prompt_ids", JSON.stringify(actionPromptIds));
      } else {
        fd.append("outfit_filter_tag_ids", JSON.stringify([]));
        fd.append("action_prompt_ids", JSON.stringify([]));
      }
      fd.append("scene", "03");
      fd.append("prompt", prompt.trim());
      for (const f of files) fd.append("files", f);
      const res = await apiFetch("/faceswap/templates/batch", { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const out = await res.json() as { count: number };
      toast(`成功上传 ${out.count}/${files.length} 个模板`, "success");
      onUploaded?.();
      onClose();
    } catch (err) {
      toast("批量上传失败：" + (err instanceof Error ? err.message : String(err)), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !submitting && onClose()} />
      <div className="relative bg-white rounded-xl w-full max-w-xl mx-4 overflow-hidden animate-[popIn_0.25s_ease-out] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">批量上传模板</h3>
          <button onClick={onClose} disabled={submitting} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><i className="ri-close-line" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">一级分类</label>
              <select required value={category} onChange={(e) => { const c = e.target.value; setCategory(c); setSubcategory((subMap[c] || [])[0] || ""); setSubsubcategory(""); if (!styleTouched) setStyle(defaultFaceswapStyle(c)); }} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                {topCategories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">二级分类</label>
              <select required value={subcategory} onChange={(e) => { setSubcategory(e.target.value); setSubsubcategory(""); }} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                {(subMap[category] || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {(() => {
            const opts = sub2Map[sub2Key(category, subcategory)] || [];
            const empty = opts.length === 0;
            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">三级分类 <span className="text-gray-400 text-xs">(可选,批量共用{empty ? "，当前二级下暂未细分" : ""})</span></label>
                <select disabled={empty} value={subsubcategory} onChange={(e) => setSubsubcategory(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 disabled:opacity-60">
                  <option value="">{empty ? "— 无三级 —" : "— 不选 —"}</option>
                  {opts.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            );
          })()}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">生成管线</label>
            <select
              required
              value={generationProvider}
              onChange={(e) => {
                const provider = e.target.value as GenerationProvider;
                if (provider === "wan") {
                  const subs = subMap["万相"] || [];
                  setGenerationProvider("wan");
                  setCategory("万相");
                  setSubcategory(subs[0] || "其他");
                  setSubsubcategory("");
                  setStyle("photo");
                  if (!bodyCoverageTagId) {
                    const defaultCoverage = getDefaultFullBodyTag(outfitTags, outfitTagGroups);
                    if (defaultCoverage) setBodyCoverageTagId(defaultCoverage.id);
                  }
                } else {
                  setGenerationProvider("facefusion");
                }
              }}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              {GENERATION_PROVIDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}（{option.description}）</option>
              ))}
            </select>
          </div>
          {generationProvider === "wan" && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    服装大类 <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={outfitTypeTagId}
                    onChange={(e) => setOutfitTypeTagId(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  >
                    <option value="">— 请选择服装大类 —</option>
                    {outfitTags
                      .filter((t) => isAvailableOutfitTypeTag(t.id, outfitTags, outfitTagGroups))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.code})
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    服装范围 <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={bodyCoverageTagId}
                    onChange={(e) => setBodyCoverageTagId(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  >
                    <option value="">— 请选择服装范围 —</option>
                    {getAvailableBodyCoverageTags(outfitTags, outfitTagGroups).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <WanActionPromptSelect
                value={actionPromptIds}
                onChange={setActionPromptIds}
                allPrompts={actionPrompts}
                disabled={submitting}
                isBatchMode
              />
              <p className="text-xs text-amber-600">
                提示：万相批量上传的模板暂不包含补充参考图，可创建后通过编辑单独添加补充图。
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">生成风格</label>
            <select required value={style} onChange={(e) => { setStyleTouched(true); setStyle(e.target.value as FaceSwapStyle); }} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
              {FACESWAP_STYLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.description})
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">模板提示词 (Prompt)</label>
              {generationProvider === "wan" && (
                <span className="text-[11px] text-teal-600 font-normal">
                  批量共用模板提示词，年龄性别、动作及固定提示词由系统自动追加
                </span>
              )}
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                generationProvider === "wan"
                  ? "批量导入的模板将共用该模板提示词（仅需描述场景环境与构图；无需写人物年龄/性别、动作及固定人体结构约束，可稍后单独编辑）..."
                  : "批量导入的模板将共用该提示词，可稍后单独编辑"
              }
              rows={3}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
            />
            <WanPromptGuidance generationProvider={generationProvider} style={style} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">选择图片（可多选）</label>
            <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-gray-200 rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 transition-colors">
              <i className="ri-upload-2-line text-2xl text-gray-400" />
              <span className="text-sm text-gray-500">点击或拖入多张图片</span>
              <span className="text-xs text-gray-400">模板名将取自文件名（去后缀）</span>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
            </div>
          </div>
          {files.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-2 max-h-56 overflow-y-auto space-y-1">
              <div className="flex items-center justify-between px-1 pb-1 text-xs text-gray-500">
                <span>已选 {files.length} 张</span>
                <button type="button" onClick={() => setFiles([])} className="text-gray-500 hover:text-red-600">清空</button>
              </div>
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded text-xs">
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="text-gray-400 mx-2">{(f.size / 1024).toFixed(0)}KB</span>
                  <button type="button" onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-600"><i className="ri-close-line" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
            <button type="submit" disabled={submitting || files.length === 0} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-60">{submitting ? "上传中..." : `上传 ${files.length} 张`}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditStyleTemplateModal({ template, open, onClose, onSave, outfitTags, outfitTagGroups, actionPrompts }: {
  template: ManagedStyleTemplate | null; open: boolean; onClose: () => void;
  onSave: (t: ManagedStyleTemplate, file?: File | null, supplementalItems?: LocalSupplementalItem[], actionPromptsTouched?: boolean) => Promise<void>;
  outfitTags: OutfitTypeTag[]; outfitTagGroups?: OutfitTagGroup[];
  actionPrompts: ActionPrompt[];
}) {
  const { topCategories, subMap, sub2Map } = useCategoryTree();
  const [form, setForm] = useState<Partial<ManagedStyleTemplate>>({});
  const [outfitTypeTagId, setOutfitTypeTagId] = useState<string>("");
  const [bodyCoverageTagId, setBodyCoverageTagId] = useState<string>("");
  const [supplementalItems, setSupplementalItems] = useState<LocalSupplementalItem[]>([]);
  const [actionPromptIds, setActionPromptIds] = useState<string[]>([]);
  const [actionPromptsTouched, setActionPromptsTouched] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [newFile, setNewFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const previewUrl = useMemo(() => (newFile ? URL.createObjectURL(newFile) : null), [newFile]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  useEffect(() => {
    if (open && template) {
      setForm({
        ...template,
        style: template.style || defaultFaceswapStyle(template.styleId),
        generationProvider: templateGenerationProvider(template),
        prompt: template.prompt || "",
      });
      setOutfitTypeTagId(template.outfitType?.id || "");
      const coverageTag = getTemplateBodyCoverageTag(template);
      setBodyCoverageTagId(coverageTag?.id || "");
      setSupplementalItems(
        (template.supplementalImages || []).map((img, idx) => ({
          key: `existing-${img.id || idx}`,
          kind: "existing",
          id: img.id,
          imageUrl: img.imageUrl,
        }))
      );
      setActionPromptIds((template.actionPrompts || []).map((p) => p.id));
      setActionPromptsTouched(false);
      setSubmitting(false);
      setNewFile(null);
    }
  }, [open, template]);
  useEffect(() => {
    if (open && template && !bodyCoverageTagId) {
      const coverageTag = getTemplateBodyCoverageTag(template, outfitTags, outfitTagGroups);
      if (coverageTag) {
        setBodyCoverageTagId(coverageTag.id);
      }
    }
  }, [open, template, bodyCoverageTagId, outfitTags, outfitTagGroups]);


  if (!open || !template) return null;

  const handleProviderChange = (newProvider: GenerationProvider) => {
    const currentProvider = form.generationProvider || "facefusion";
    if (currentProvider === "wan" && newProvider === "facefusion") {
      const hasSupplemental = supplementalItems.length > 0;
      const hasOutfit = !!outfitTypeTagId;
      const hasCoverage = !!bodyCoverageTagId;
      if (hasSupplemental || hasOutfit || hasCoverage) {
        const ok = window.confirm("切换为 FaceFusion 将清空该模板关联的服装大类、服装范围和补充参考图，是否确认切换？");
        if (!ok) {
          return;
        }
        setOutfitTypeTagId("");
        setBodyCoverageTagId("");
        setSupplementalItems([]);
      }
      setActionPromptIds([]);
      setActionPromptsTouched(true);
    } else if (newProvider === "wan") {
      const subs = subMap["万相"] || [];
      setForm((f) => ({
        ...f,
        generationProvider: "wan",
        styleId: "万相",
        styleName: "万相",
        style: "photo",
        subcategory: subs.includes(f.subcategory || "") ? f.subcategory : (subs[0] || "其他"),
        subsubcategory: "",
      }));
      if (!bodyCoverageTagId) {
        const defaultCoverage = getDefaultFullBodyTag(outfitTags, outfitTagGroups);
        if (defaultCoverage) setBodyCoverageTagId(defaultCoverage.id);
      }
      setActionPromptIds([]);
      setActionPromptsTouched(true);
      return;
    }
    setForm((f) => ({ ...f, generationProvider: newProvider }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const effectiveProvider = form.generationProvider || "facefusion";
    const effectiveCategory = form.styleId || template.styleId;
    const effectiveStyle = form.style || "chibi";

    if (effectiveProvider === "wan") {
      if (effectiveCategory !== "万相" || effectiveStyle !== "photo") {
        toast("万相模板必须归入“万相”一级分类且风格为纯换脸(photo)", "error");
        return;
      }
      if (!outfitTypeTagId) {
        toast("万相模板必须选择服装大类", "error");
        return;
      }
      if (!isAvailableOutfitTypeTag(outfitTypeTagId, outfitTags, outfitTagGroups)) {
        toast("当前关联的服装大类不可用，请重新选择后再保存", "error");
        return;
      }
      if (!bodyCoverageTagId) {
        toast("万相模板必须选择服装范围", "error");
        return;
      }
      if (!isAvailableBodyCoverageTag(bodyCoverageTagId, outfitTags, outfitTagGroups)) {
        toast("当前关联的服装范围不可用，请重新选择后再保存", "error");
        return;
      }
    }

    const selectedCoverageTag =
      outfitTags.find((t) => t.id === bodyCoverageTagId) ||
      template.outfitFilterTags?.find((t) => t.id === bodyCoverageTagId);
    const updatedCoverageTag = selectedCoverageTag
      ? {
          id: selectedCoverageTag.id,
          code: selectedCoverageTag.code,
          name: selectedCoverageTag.name,
          groupCode: "body_coverage",
          groupId: selectedCoverageTag.groupId,
          enabled: selectedCoverageTag.enabled,
        }
      : null;

    const coverageGroupId = outfitTagGroups?.find((g) => g.code === "body_coverage")?.id;
    const isCoverageFilterTag = (t: OutfitTypeTag) => {
      if (t.groupCode === "body_coverage") return true;
      if (coverageGroupId && t.groupId === coverageGroupId) return true;
      if (outfitTags) {
        const fullTag = outfitTags.find((tag) => tag.id === t.id);
        if (fullTag?.groupCode === "body_coverage") return true;
        if (coverageGroupId && fullTag?.groupId === coverageGroupId) return true;
      }
      return false;
    };
    const nonCoverageFilterTags = (template.outfitFilterTags || []).filter((t) => !isCoverageFilterTag(t));
    const nextFilterTags = effectiveProvider === "wan"
      ? (updatedCoverageTag ? [...nonCoverageFilterTags, updatedCoverageTag] : nonCoverageFilterTags)
      : [];

    const selectedTag = outfitTags.find((t) => t.id === outfitTypeTagId);
    const updatedOutfitType = selectedTag ? { id: selectedTag.id, code: selectedTag.code, name: selectedTag.name } : null;
    if (
      effectiveProvider === "wan" &&
      template.outfitType?.id &&
      template.outfitType.id !== outfitTypeTagId &&
      !window.confirm(`该万相模板的服装大类将从“${template.outfitType?.name || "未设置"}”改为“${selectedTag!.name}”，是否确认保存？`)
    ) {
      return;
    }

    const nextActionPrompts: ActionPromptRelation[] = effectiveProvider === "wan"
      ? actionPromptIds.map((id) => {
          const existing = (template.actionPrompts || []).find((p) => p.id === id);
          if (existing) return existing;
          const libPrompt = actionPrompts.find((p) => p.id === id);
          if (libPrompt) return { id: libPrompt.id, name: libPrompt.name, text: libPrompt.text, enabled: libPrompt.enabled, sortOrder: libPrompt.sortOrder };
          return { id, name: id, text: "", enabled: true, sortOrder: 0 };
        })
      : [];

    const updated: ManagedStyleTemplate = {
      ...template,
      ...form,
      styleId: effectiveCategory,
      styleName: effectiveCategory,
      generationProvider: effectiveProvider,
      outfitType: effectiveProvider === "wan" ? updatedOutfitType : null,
      outfitFilterTags: nextFilterTags,
      supplementalImages: effectiveProvider === "wan"
        ? supplementalItems
            .filter((i) => i.kind === "existing")
            .map((i, idx) => ({ id: i.id!, imageUrl: i.imageUrl!, sortOrder: idx }))
        : [],
      actionPrompts: nextActionPrompts,
    } as ManagedStyleTemplate;

    setSubmitting(true);
    try {
      await onSave(updated, newFile, supplementalItems, actionPromptsTouched);
      toast("模板更新成功", "success");
      onClose();
    } catch (err) {
      toast("保存失败：" + (err instanceof Error ? err.message : String(err)), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-lg mx-4 overflow-hidden animate-[popIn_0.25s_ease-out] max-h-[90vh] overflow-y-auto">
        <style>{`@keyframes popIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">编辑模板</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><i className="ri-close-line" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">参考图</label>
              <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(e) => setNewFile(e.target.files?.[0] ?? null)} />
              <div className="flex items-center gap-4">
                <div className="w-28 h-28 rounded-lg bg-gradient-to-b from-slate-50 to-slate-200 border border-gray-200 overflow-hidden flex items-center justify-center">
                  <img src={previewUrl || template.image} alt="参考图" className="w-full h-full object-contain p-1" />
                </div>
                <div className="space-y-1.5">
                  <button type="button" onClick={() => fileInput.current?.click()} className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100">
                    <i className="ri-image-edit-line mr-1" />更换图片
                  </button>
                  {newFile && <p className="text-xs text-amber-600">已选择新图：{newFile.name}（保存后生效）</p>}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">模板名称</label>
              <input type="text" required value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">一级分类</label>
                <select required value={form.styleId || topCategories[0] || ""} onChange={(e) => { const styleId = e.target.value; const oldCat = form.styleId || ""; const currentStyle = form.style || "chibi"; const nextStyle = currentStyle === defaultFaceswapStyle(oldCat) ? defaultFaceswapStyle(styleId) : currentStyle; const subs = subMap[styleId] || []; setForm((f) => ({ ...f, styleId, styleName: styleId, style: nextStyle, subcategory: subs.includes(f.subcategory || "") ? f.subcategory : (subs[0] || ""), subsubcategory: "" })); }} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                  {topCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">二级分类</label>
                <select required value={form.subcategory || (subMap[form.styleId || topCategories[0] || ""] || [""])[0]} onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value, subsubcategory: "" }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                  {(subMap[form.styleId || topCategories[0] || ""] || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {(() => {
              const curCat = form.styleId || topCategories[0] || "";
              const curSub = form.subcategory || (subMap[curCat] || [""])[0];
              const opts = sub2Map[sub2Key(curCat, curSub)] || [];
              const empty = opts.length === 0;
              return (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">三级分类 <span className="text-gray-400 text-xs">(可选{empty ? "，当前二级下暂未细分" : ""})</span></label>
                  <select disabled={empty} value={form.subsubcategory || ""} onChange={(e) => setForm((f) => ({ ...f, subsubcategory: e.target.value }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 disabled:opacity-60">
                    <option value="">{empty ? "— 无三级 —" : "— 不选 —"}</option>
                    {opts.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              );
            })()}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">生成风格</label>
              <select required value={form.style || "chibi"} onChange={(e) => setForm((f) => ({ ...f, style: e.target.value }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                {FACESWAP_STYLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} ({opt.description})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">生成管线</label>
              <select
                required
                value={form.generationProvider || "facefusion"}
                onChange={(e) => handleProviderChange(e.target.value as GenerationProvider)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                {GENERATION_PROVIDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}（{option.description}）</option>
                ))}
              </select>
            </div>
            {form.generationProvider === "wan" && (
              <WanTemplateGenerationFields
                outfitTypeTagId={outfitTypeTagId}
                onOutfitTypeTagIdChange={setOutfitTypeTagId}
                bodyCoverageTagId={bodyCoverageTagId}
                onBodyCoverageTagIdChange={setBodyCoverageTagId}
                templateFilterTags={template.outfitFilterTags}
                outfitTags={outfitTags}
                outfitTagGroups={outfitTagGroups}
                supplementalItems={supplementalItems}
                onSupplementalItemsChange={setSupplementalItems}
                actionPromptIds={actionPromptIds}
                onActionPromptIdsChange={(ids) => {
                  setActionPromptIds(ids);
                  setActionPromptsTouched(true);
                }}
                allActionPrompts={actionPrompts}
                templateActionPrompts={template.actionPrompts || []}
                disabled={submitting}
              />
            )}
            <div>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">模板提示词 (Prompt)</label>
                {form.generationProvider === "wan" && (
                  <span className="text-[11px] text-teal-600 font-normal">
                    仅需填写场景与构图描述，年龄性别、动作及固定提示词由系统自动追加
                  </span>
                )}
              </div>
              <textarea
                value={form.prompt || ""}
                onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
                placeholder={
                  form.generationProvider === "wan"
                    ? "请输入模板提示词（专注场景构图、机位光影与服装融合；无需写人物年龄/性别、动作姿态及固定人体结构约束）..."
                    : "请输入提示词..."
                }
                rows={3}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
              />
              <WanPromptGuidance generationProvider={form.generationProvider} style={form.style} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">审核状态</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={form.complianceStatus === "approved"} onChange={() => setForm((f) => ({ ...f, complianceStatus: "approved" }))} className="w-4 h-4 text-teal-600" /><span className="text-sm text-gray-700">已通过</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={form.complianceStatus === "pending"} onChange={() => setForm((f) => ({ ...f, complianceStatus: "pending" }))} className="w-4 h-4 text-teal-600" /><span className="text-sm text-gray-700">待审核</span></label>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">启用状态</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={form.enabled || false} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} className="sr-only peer" />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
              </label>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
              <button type="submit" disabled={submitting} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-60">{submitting ? "保存中..." : "保存修改"}</button>
            </div>
          </form>
      </div>
    </div>
  );
}

function EditPureTemplateModal({ template, open, onClose, onSave }: {
  template: PureSwapTemplate | null; open: boolean; onClose: () => void; onSave: (t: PureSwapTemplate) => void;
}) {
  const [form, setForm] = useState<Partial<PureSwapTemplate>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open && template) { setForm({ ...template }); setSubmitted(false); }
  }, [open, template]);

  if (!open || !template) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    onSave({ ...template, ...form } as PureSwapTemplate);
    setTimeout(() => { setSubmitted(false); onClose(); }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-lg mx-4 overflow-hidden animate-[popIn_0.25s_ease-out] max-h-[90vh] overflow-y-auto">
        <style>{`@keyframes popIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">编辑场景</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><i className="ri-close-line" /></button>
        </div>
        {submitted ? (
          <div className="p-10 flex flex-col items-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full"><i className="ri-check-line text-emerald-600 text-2xl" /></div>
            <p className="text-sm font-medium text-gray-900">场景更新成功</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">场景名称</label>
              <input type="text" required value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">场景类型</label>
              <select required value={form.sceneType || ""} onChange={(e) => setForm((f) => ({ ...f, sceneType: e.target.value }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                <option value="山水风景">山水风景</option>
                <option value="城市风光">城市风光</option>
                <option value="日出日落">日出日落</option>
                <option value="名胜古迹">名胜古迹</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">场景描述</label>
              <textarea value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">人脸位置</label>
              <select value={form.facePosition || "center"} onChange={(e) => setForm((f) => ({ ...f, facePosition: e.target.value as "center" | "left" | "right" }))} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                <option value="center">居中</option>
                <option value="left">左侧</option>
                <option value="right">右侧</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">审核状态</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={form.complianceStatus === "approved"} onChange={() => setForm((f) => ({ ...f, complianceStatus: "approved" }))} className="w-4 h-4 text-teal-600" /><span className="text-sm text-gray-700">已通过</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={form.complianceStatus === "pending"} onChange={() => setForm((f) => ({ ...f, complianceStatus: "pending" }))} className="w-4 h-4 text-teal-600" /><span className="text-sm text-gray-700">待审核</span></label>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">启用状态</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={form.enabled || false} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} className="sr-only peer" />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
              </label>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
              <button type="submit" className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">保存修改</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════  FACE SWAP SIMULATION MODAL  ═══════════════════ */
function FaceSwapModal({ template, open, onClose }: { template: PureSwapTemplate | null; open: boolean; onClose: () => void }) {
  const [step, setStep] = useState<"upload" | "processing" | "result">("upload");
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (open) { setStep("upload"); setSelectedPhoto(null); setProgress(0); } }, [open]);

  useEffect(() => {
    if (step === "processing") {
      let p = 0;
      const interval = setInterval(() => {
        p += Math.random() * 15 + 5;
        if (p >= 100) { p = 100; clearInterval(interval); setTimeout(() => setStep("result"), 400); }
        setProgress(Math.min(p, 100));
      }, 300);
      return () => clearInterval(interval);
    }
  }, [step]);

  if (!open || !template) return null;

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedPhoto(URL.createObjectURL(file));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-2xl mx-4 overflow-hidden animate-[popIn_0.25s_ease-out]">
        <style>{`@keyframes popIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-teal-100 rounded-lg"><i className="ri-image-edit-line text-teal-600" /></div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">模拟换脸</h3>
              <p className="text-xs text-gray-500">{template.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><i className="ri-close-line" /></button>
        </div>

        {step === "upload" && (
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">模板</p>
                <div className="relative rounded-lg overflow-hidden">
                  <img src={template.image} alt={template.name} className="w-full h-56 object-cover" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-2 border-white/70 border-dashed animate-[spin_8s_linear_infinite]" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] text-white font-medium bg-black/40 px-1.5 py-0.5 rounded">人脸区域</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">选择游客照片</p>
                {selectedPhoto ? (
                  <div className="relative rounded-lg overflow-hidden">
                    <img src={selectedPhoto} alt="游客照片" className="w-full h-56 object-cover" />
                    <button onClick={() => { setSelectedPhoto(null); if (fileRef.current) fileRef.current.value = ""; }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors">
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-gray-200 rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 transition-colors">
                      <i className="ri-upload-cloud-line text-2xl text-gray-400" />
                      <span className="text-sm text-gray-500">点击上传照片</span>
                      <span className="text-xs text-gray-400">或拖拽到此处</span>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-2">使用示例照片</p>
                      <div className="flex gap-2">
                        {mockUserPhotosSeed.map((photo) => (
                          <button key={photo.id} onClick={() => setSelectedPhoto(photo.url)} className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${selectedPhoto === photo.url ? "border-teal-500 ring-2 ring-teal-500/20" : "border-transparent hover:border-gray-300"}`}>
                            <img src={photo.url} alt={photo.name} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
              <button onClick={() => { if (selectedPhoto) setStep("processing"); }} disabled={!selectedPhoto} className={`px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors whitespace-nowrap flex items-center gap-2 ${selectedPhoto ? "bg-teal-600 hover:bg-teal-700" : "bg-gray-300 cursor-not-allowed"}`}>
                <i className="ri-magic-line" /> 开始换脸
              </button>
            </div>
          </div>
        )}

        {step === "processing" && (
          <div className="p-10 flex flex-col items-center gap-4">
            <div className="w-16 h-16 relative">
              <div className="absolute inset-0 rounded-full border-4 border-teal-100" />
              <div className="absolute inset-0 rounded-full border-4 border-teal-600 border-t-transparent animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center"><i className="ri-magic-line text-teal-600 text-xl" /></div>
            </div>
            <p className="text-sm font-medium text-gray-700">AI 正在生成换脸效果...</p>
            <div className="w-64 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-teal-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-xs text-gray-400">{Math.round(progress)}%</p>
          </div>
        )}

        {step === "result" && (
          <div className="p-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-2 text-center">原模板</p>
                <div className="rounded-lg overflow-hidden"><img src={template.image} alt="原模板" className="w-full h-40 object-cover" /></div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2 text-center">游客照片</p>
                <div className="rounded-lg overflow-hidden"><img src={selectedPhoto || ""} alt="游客照片" className="w-full h-40 object-cover" /></div>
              </div>
              <div>
                <p className="text-xs text-teal-600 mb-2 text-center font-medium">换脸结果</p>
                <div className="relative rounded-lg overflow-hidden">
                  <img src={template.image} alt="换脸结果" className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-teal-600/20 to-transparent" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border-2 border-teal-500 bg-white/80 flex items-center justify-center">
                    <img src={selectedPhoto || ""} alt="脸" className="w-10 h-10 rounded-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => setStep("upload")} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-refresh-line" /> 换一张
              </button>
              <button onClick={onClose} className="px-6 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-check-line" /> 完成
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════  TEMPLATE PREVIEW MODAL  ═══════════════════ */
function TemplatePreviewModal({ template, open, onClose }: {
  template: StyleTemplate | PureSwapTemplate | null;
  open: boolean;
  onClose: () => void;
}) {
  const [selectedEffect, setSelectedEffect] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  if (!open || !template) return null;

  const effects = [
    { name: "游客A", photo: mockUserPhotosSeed[0].url },
    { name: "游客B", photo: mockUserPhotosSeed[1].url },
    { name: "游客C", photo: mockUserPhotosSeed[2].url },
  ];

  const handleDownload = () => {
    setToast("合成效果图已下载");
    setTimeout(() => setToast(null), 2000);
  };

  const handleShare = () => {
    setToast("链接已复制到剪贴板");
    setTimeout(() => setToast(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-3xl mx-4 overflow-hidden animate-[popIn_0.25s_ease-out]">
        <style>{`@keyframes popIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">换脸合成效果预览</h3>
            <p className="text-xs text-gray-500 mt-0.5">{template.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <i className="ri-close-line" />
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">原模板</p>
              <div className="relative rounded-xl overflow-hidden">
                <img src={template.image} alt="原模板" className="w-full h-72 object-cover" />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">合成效果</p>
              <div className="relative rounded-xl overflow-hidden">
                <img src={template.image} alt="合成效果" className="w-full h-72 object-cover" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg overflow-hidden">
                      <img src={effects[selectedEffect].photo} alt="游客脸" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-teal-600 rounded-full text-[10px] text-white whitespace-nowrap">AI 合成</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="text-xs text-gray-500">选择游客照片：</span>
            {effects.map((eff, i) => (
              <button key={eff.name} onClick={() => setSelectedEffect(i)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedEffect === i ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                <img src={eff.photo} alt={eff.name} className="w-5 h-5 rounded-full object-cover" />
                {eff.name}
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <button onClick={handleDownload} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-download-line" /> 下载
            </button>
            <button onClick={handleShare} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-share-line" /> 分享
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">合成效果为示意图，实际生成效果以 AI 处理为准</p>
          {toast && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm shadow-lg animate-[fadeIn_0.2s_ease-out]">
              <style>{`@keyframes fadeIn { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }`}</style>
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-emerald-400" />
                {toast}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════  HOT RANK  ═══════════════════ */
function StyleHotRank({ collections }: { collections: StyleCollection[] }) {
  const top5 = useMemo(() => [...collections].sort((a, b) => b.generateCount - a.generateCount).slice(0, 5), [collections]);
  const max = Math.max(...top5.map((c) => c.generateCount));

  const rankColors = [
    "bg-amber-500 text-white",
    "bg-gray-400 text-white",
    "bg-amber-700 text-white",
    "bg-gray-200 text-gray-700",
    "bg-gray-200 text-gray-700",
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <i className="ri-fire-line text-rose-500 text-lg" />
        <h3 className="text-sm font-semibold text-gray-900">风格热度排行榜</h3>
        <span className="text-xs text-gray-400">按总生成量排序</span>
      </div>
      <div className="space-y-3">
        {top5.map((c, i) => {
          const pct = (c.generateCount / max) * 100;
          return (
            <div key={c.id} className="flex items-center gap-3">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${rankColors[i]}`}>
                {i === 0 ? <i className="ri-vip-crown-line text-sm" /> : i === 1 ? <i className="ri-medal-line text-sm" /> : i === 2 ? <i className="ri-trophy-line text-sm" /> : <span className="text-xs font-bold">{i + 1}</span>}
              </div>
              <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                <img src={c.coverImage} alt={c.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate">{c.name}</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">{c.templateCount} 个模板</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-amber-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-12 text-right tabular-nums">{c.generateCount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════  HOT RANK  ═══════════════════ */
function HotRank({ templates }: { templates: PureSwapTemplate[] }) {
  const top5 = useMemo(() => [...templates].sort((a, b) => b.generateCount - a.generateCount).slice(0, 5), [templates]);
  const max = Math.max(...top5.map((t) => t.generateCount));

  const rankColors = [
    "bg-amber-500 text-white",
    "bg-gray-400 text-white",
    "bg-amber-700 text-white",
    "bg-gray-200 text-gray-700",
    "bg-gray-200 text-gray-700",
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <i className="ri-fire-line text-rose-500 text-lg" />
        <h3 className="text-sm font-semibold text-gray-900">场景热度排行榜</h3>
        <span className="text-xs text-gray-400">按总生成量排序</span>
      </div>
      <div className="space-y-3">
        {top5.map((t, i) => {
          const pct = (t.generateCount / max) * 100;
          const sceneBadge = getSceneTypeColor(t.sceneType);
          return (
            <div key={t.id} className="flex items-center gap-3">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${rankColors[i]}`}>
                {i === 0 ? <i className="ri-vip-crown-line text-sm" /> : i === 1 ? <i className="ri-medal-line text-sm" /> : i === 2 ? <i className="ri-trophy-line text-sm" /> : <span className="text-xs font-bold">{i + 1}</span>}
              </div>
              <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate">{t.name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${sceneBadge}`}>{getSceneTypeLabel(t.sceneType)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-rose-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-12 text-right tabular-nums">{t.generateCount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════  CATEGORIES MANAGER MODAL  ═══════════════════ */
// 分类管理：三级树 CRUD(最多三层)。改名事务同步 faceswap_templates 对应列;
// 有子分类或有引用模板时删除会被后端拒绝，弹 toast 提示。
function CategoriesManagerModal({ open, onClose, onChanged }: {
  open: boolean; onClose: () => void; onChanged: () => void;
}) {
  const { tree, reload } = useCategoryTree();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      // Default: expand all top nodes for quick overview.
      setExpanded(new Set(tree.map((t) => t.id)));
    }
  }, [open, tree]);

  if (!open) return null;

  const notify = () => { reload(); onChanged(); };

  const addTop = async () => {
    const name = window.prompt("新一级分类名称？")?.trim();
    if (!name) return;
    setBusy(true);
    try {
      const res = await apiFetch("/faceswap/categories", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parentId: "", name, sortOrder: (tree.length + 1) * 10 }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast("已新增一级分类", "success");
      notify();
    } catch (e) {
      toast("新增失败：" + (e instanceof Error ? e.message : String(e)), "error");
    } finally { setBusy(false); }
  };

  const addSub = async (parent: FSCategoryNode, level: 2 | 3) => {
    const name = window.prompt(`在「${parent.name}」下新增${level === 2 ? "二" : "三"}级分类，请输入名称：`)?.trim();
    if (!name) return;
    setBusy(true);
    try {
      const res = await apiFetch("/faceswap/categories", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parentId: parent.id, name, sortOrder: ((parent.children?.length || 0) + 1) * 10 }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast(`已新增${level === 2 ? "二" : "三"}级分类`, "success");
      notify();
    } catch (e) {
      toast("新增失败：" + (e instanceof Error ? e.message : String(e)), "error");
    } finally { setBusy(false); }
  };

  const rename = async (node: FSCategoryNode) => {
    const name = window.prompt(`重命名「${node.name}」为：`, node.name)?.trim();
    if (!name || name === node.name) return;
    if (!window.confirm(`确认将「${node.name}」改为「${name}」？关联模板会一并同步。`)) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/faceswap/categories/${node.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast("已重命名（关联模板已同步）", "success");
      notify();
    } catch (e) {
      toast("重命名失败：" + (e instanceof Error ? e.message : String(e)), "error");
    } finally { setBusy(false); }
  };

  const remove = async (node: FSCategoryNode) => {
    if (!window.confirm(`确认删除分类「${node.name}」？（下面还有子分类或已引用模板时会被拒绝）`)) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/faceswap/categories/${node.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
      toast("已删除", "success");
      notify();
    } catch (e) {
      toast("删除失败：" + (e instanceof Error ? e.message : String(e)), "error");
    } finally { setBusy(false); }
  };

  const move = async (list: FSCategoryNode[], idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    // 交换 sort_order（若相同则给 a 更大/更小）。
    const orderA = b.sortOrder, orderB = a.sortOrder;
    setBusy(true);
    try {
      await apiFetch(`/faceswap/categories/${a.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sortOrder: orderA }),
      });
      await apiFetch(`/faceswap/categories/${b.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sortOrder: orderB }),
      });
      notify();
    } catch (e) {
      toast("排序失败：" + (e instanceof Error ? e.message : String(e)), "error");
    } finally { setBusy(false); }
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !busy && onClose()} />
      <div className="relative bg-white rounded-xl w-full max-w-2xl mx-4 overflow-hidden animate-[popIn_0.25s_ease-out] max-h-[85vh] flex flex-col">
        <style>{`@keyframes popIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">分类管理</h3>
            <p className="text-xs text-gray-500 mt-0.5">三级分类(最多)。改名会同步已关联的模板；有子分类或模板时无法删除。</p>
          </div>
          <button onClick={onClose} disabled={busy} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><i className="ri-close-line" /></button>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">{tree.length} 个一级分类</span>
          <button onClick={addTop} disabled={busy} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors flex items-center gap-1 disabled:opacity-60">
            <i className="ri-add-line" /> 新增一级分类
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {tree.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">还没有分类，点右上「新增一级分类」开始配置</div>
          )}
          {tree.map((top, ti) => {
            const isOpen = expanded.has(top.id);
            const children = top.children || [];
            return (
              <div key={top.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                  <button onClick={() => toggleExpand(top.id)} className="flex items-center gap-2 text-left flex-1">
                    <i className={`ri-arrow-${isOpen ? "down" : "right"}-s-line text-gray-500`} />
                    <span className="text-sm font-medium text-gray-800">{top.name}</span>
                    <span className="text-xs text-gray-400">({children.length})</span>
                  </button>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(tree, ti, -1)} disabled={busy || ti === 0} className="p-1.5 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="上移"><i className="ri-arrow-up-line" /></button>
                    <button onClick={() => move(tree, ti, 1)} disabled={busy || ti === tree.length - 1} className="p-1.5 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="下移"><i className="ri-arrow-down-line" /></button>
                    <button onClick={() => addSub(top, 2)} disabled={busy} className="px-2 py-1 text-teal-600 hover:bg-teal-50 rounded text-xs font-medium" title="新增二级子分类"><i className="ri-add-line" /></button>
                    <button onClick={() => rename(top)} disabled={busy} className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded text-xs" title="重命名"><i className="ri-edit-line" /></button>
                    <button onClick={() => remove(top)} disabled={busy} className="px-2 py-1 text-red-500 hover:bg-red-50 rounded text-xs" title="删除"><i className="ri-delete-bin-line" /></button>
                  </div>
                </div>
                {isOpen && (
                  <div className="p-2 space-y-1">
                    {children.length === 0 && (
                      <div className="text-xs text-gray-400 px-3 py-2">还没有子分类</div>
                    )}
                    {children.map((sub, si) => {
                      const subKids = sub.children || [];
                      const subOpen = expanded.has(sub.id);
                      return (
                        <div key={sub.id} className="border border-transparent hover:border-gray-200 rounded">
                          <div className="flex items-center justify-between px-3 py-2 rounded hover:bg-gray-50">
                            <button onClick={() => toggleExpand(sub.id)} className="flex items-center gap-2 flex-1 text-left">
                              <i className={`ri-arrow-${subOpen ? "down" : "right"}-s-line text-gray-400 text-xs ${subKids.length === 0 ? "invisible" : ""}`} />
                              <span className="text-sm text-gray-700">{sub.name}</span>
                              {subKids.length > 0 && <span className="text-xs text-gray-400">({subKids.length})</span>}
                            </button>
                            <div className="flex items-center gap-1">
                              <button onClick={() => move(children, si, -1)} disabled={busy || si === 0} className="p-1.5 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="上移"><i className="ri-arrow-up-line" /></button>
                              <button onClick={() => move(children, si, 1)} disabled={busy || si === children.length - 1} className="p-1.5 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="下移"><i className="ri-arrow-down-line" /></button>
                              <button onClick={() => addSub(sub, 3)} disabled={busy} className="px-2 py-1 text-teal-600 hover:bg-teal-50 rounded text-xs font-medium" title="新增三级子分类"><i className="ri-add-line" /></button>
                              <button onClick={() => rename(sub)} disabled={busy} className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded text-xs" title="重命名"><i className="ri-edit-line" /></button>
                              <button onClick={() => remove(sub)} disabled={busy} className="px-2 py-1 text-red-500 hover:bg-red-50 rounded text-xs" title="删除"><i className="ri-delete-bin-line" /></button>
                            </div>
                          </div>
                          {subOpen && subKids.length > 0 && (
                            <div className="pl-6 pr-2 py-1 space-y-1 bg-gray-50/50">
                              {subKids.map((leaf, li) => (
                                <div key={leaf.id} className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-white">
                                  <span className="text-xs text-gray-600">{leaf.name}</span>
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => move(subKids, li, -1)} disabled={busy || li === 0} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="上移"><i className="ri-arrow-up-line text-xs" /></button>
                                    <button onClick={() => move(subKids, li, 1)} disabled={busy || li === subKids.length - 1} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="下移"><i className="ri-arrow-down-line text-xs" /></button>
                                    <button onClick={() => rename(leaf)} disabled={busy} className="px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 rounded text-xs" title="重命名"><i className="ri-edit-line text-xs" /></button>
                                    <button onClick={() => remove(leaf)} disabled={busy} className="px-1.5 py-0.5 text-red-500 hover:bg-red-50 rounded text-xs" title="删除"><i className="ri-delete-bin-line text-xs" /></button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════  AUGC OUTFIT LIBRARY  ═══════════════════ */
type OutfitSelectionMode = "single" | "multiple";
type OutfitMatchMode = "include";
interface AugcOutfitTagGroup { id: string; code: string; name: string; selectionMode: OutfitSelectionMode; matching?: boolean; matchEnabled?: boolean; enabled?: boolean; tags?: AugcOutfitTag[]; }
interface AugcOutfitTag { id: string; groupId: string; groupCode?: string; code: string; name: string; enabled: boolean; }
interface AugcOutfitConstraint { groupCode: string; mode: OutfitMatchMode; tagCodes: string[]; }
interface AugcOutfit { id: string; name: string; displayImageUrl: string; enabled: boolean; tagCodes: string[]; tags?: AugcOutfitTag[]; tagAssignments?: AugcOutfitTag[]; constraints?: Record<string, OutfitMatchMode>; matchConstraints?: AugcOutfitConstraint[]; createdAt?: string; }
interface AugcOutfitListResponse { items: AugcOutfit[]; facets: AugcOutfitTagGroup[]; page: number; pageSize: number; total: number; }
const OUTFIT_FALLBACK_GROUPS: AugcOutfitTagGroup[] = [{ id: "occasion", code: "occasion", name: "场合", selectionMode: "single", matchEnabled: true, enabled: true }];
const OUTFIT_FALLBACK_TAGS: AugcOutfitTag[] = [{ id: "ceremony", groupId: "occasion", groupCode: "occasion", code: "ceremony", name: "仪式", enabled: true }];
const OUTFIT_FALLBACK_LIST: AugcOutfitListResponse = { items: [{ id: "outfit-fallback", name: "示例礼服", displayImageUrl: "", enabled: true, tagCodes: ["ceremony"], tags: OUTFIT_FALLBACK_TAGS, constraints: { occasion: "include" } }], facets: OUTFIT_FALLBACK_GROUPS, page: 1, pageSize: 20, total: 1 };

function normalizeOutfit(raw: AugcOutfit): AugcOutfit {
  const assignments = raw.tagAssignments || raw.tags || [];
  return {
    id: String(raw.id), name: raw.name || "", displayImageUrl: raw.displayImageUrl || "",
    enabled: raw.enabled !== false,
    tagCodes: raw.tagCodes || assignments.map((tag) => tag.code),
    tagAssignments: assignments,
    matchConstraints: raw.matchConstraints || Object.entries(raw.constraints || {}).map(([groupCode, mode]) => ({ groupCode, mode, tagCodes: [] })),
    createdAt: raw.createdAt,
  };
}
function OutfitEditorModal({ outfit, groups, tags, onClose, onSaved }: {
  outfit: AugcOutfit | null; groups: AugcOutfitTagGroup[]; tags: AugcOutfitTag[]; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(outfit?.name || "");
  const [enabled, setEnabled] = useState(outfit?.enabled ?? true);
  const [tagIds, setTagIds] = useState<string[]>(outfit?.tagAssignments?.map((tag) => tag.id) || []);
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const preview = useMemo(() => file ? URL.createObjectURL(file) : outfit?.displayImageUrl, [file, outfit]);
  useEffect(() => () => { if (file && preview) URL.revokeObjectURL(preview); }, [file, preview]);

  const enabledGroups = useMemo(() => groups.filter((group) => group.enabled !== false), [groups]);
  const matchingGroups = useMemo(
    () => enabledGroups.filter((group) => group.matchEnabled ?? group.matching ?? false),
    [enabledGroups],
  );
  const tagForGroup = (group: AugcOutfitTagGroup) => tags.filter((tag) => tag.enabled && (tag.groupId === group.id || tag.groupCode === group.code));
  const toggleTag = (group: AugcOutfitTagGroup, id: string) => setTagIds((current) => {
    const groupIds = new Set(tagForGroup(group).map((tag) => tag.id));
    if (group.selectionMode === "single") return [...current.filter((item) => !groupIds.has(item)), ...(current.includes(id) ? [] : [id])];
    return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
  });
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) { toast("请填写服装名称", "error"); return; }
    const missingGroup = matchingGroups.find((group) => !tagIds.some((id) => tagForGroup(group).some((tag) => tag.id === id && tag.enabled)));
    if (missingGroup) { toast(`请选择「${missingGroup.name}」标签`, "error"); return; }
    let id = outfit?.id;
    const created = !id;
    try {
      const payload = { name: name.trim(), enabled };
      if (id) {
        const response = await apiFetch(`/augc/outfits/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error(await response.text());
      } else {
        const response = await apiFetch("/augc/outfits", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error(await response.text());
        id = (await response.json()).id;
      }
      if (!id) throw new Error("服务未返回服装 ID");
      if (file) {
        const data = new FormData(); data.append("file", file);
        const response = await apiFetch(`/augc/outfits/${id}/display-image`, { method: "POST", body: data });
        if (!response.ok) throw new Error(await response.text());
      }
      const enabledTagIds = new Set(enabledGroups.flatMap((group) => tagForGroup(group).map((tag) => tag.id)));
      const effectiveConstraints = Object.fromEntries(matchingGroups.map((group) => [group.code, "include"]));
      const response = await apiFetch(`/augc/outfits/${id}/tag-config`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tagIds: tagIds.filter((tagId) => enabledTagIds.has(tagId)), constraints: effectiveConstraints }) });
      if (!response.ok) throw new Error(await response.text());
      toast(outfit ? "服装已更新" : "服装已创建", "success"); onSaved(); onClose();
    } catch (error) {
      if (created && id) await apiFetch(`/augc/outfits/${id}`, { method: "DELETE" }).catch(() => undefined);
      toast("保存失败：" + (error instanceof Error ? error.message : String(error)), "error");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={save} className="relative bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto animate-[popIn_0.25s_ease-out]">
        <div className="flex items-center justify-between p-5 border-b border-gray-100"><h3 className="text-base font-semibold text-gray-900">{outfit ? "编辑服装" : "新建服装"}</h3><button type="button" onClick={onClose} className="w-8 h-8 text-gray-400 hover:text-gray-600"><i className="ri-close-line" /></button></div>
        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-[140px_1fr] gap-4">
            <div><input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(event) => setFile(event.target.files?.[0] || null)} /><button type="button" onClick={() => fileInput.current?.click()} className="w-32 h-32 rounded-lg border border-dashed border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center text-gray-400 hover:border-teal-400">{preview ? <img src={preview} alt="服装展示图" className="w-full h-full object-cover" /> : <span className="text-xs"><i className="ri-upload-cloud-line block text-xl mb-1" />上传展示图</span>}</button><p className="mt-1 text-[11px] text-gray-400">仅供管理展示，不作为模型输入</p></div>
            <div className="space-y-3"><div><label className="block text-sm font-medium text-gray-700 mb-1">服装名称</label><input required value={name} onChange={(event) => setName(event.target.value)} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20" /></div><label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} className="accent-teal-600" />启用此服装</label></div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">服装标签</h4>
            <div className="space-y-3">
              {enabledGroups.map((group) => {
                const isSingle = group.selectionMode === "single";
                const isMatching = Boolean(group.matchEnabled ?? group.matching ?? false);
                const groupTags = tagForGroup(group);
                return (
                  <div key={group.id} className="p-3 bg-gray-50/70 border border-gray-100 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-gray-900">{group.name}</span>
                      {isMatching ? (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                          <span className="text-rose-600 font-bold">*</span>必选匹配
                        </span>
                      ) : null}
                      {isSingle ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          单选
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                          多选
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {groupTags.map((tag) => {
                        const isSelected = tagIds.includes(tag.id);
                        return (
                          <button
                            type="button"
                            key={tag.id}
                            onClick={() => toggleTag(group, tag.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              isSelected
                                ? isSingle
                                  ? "bg-teal-600 text-white shadow-sm ring-2 ring-teal-600/30"
                                  : "bg-teal-600 text-white shadow-sm"
                                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {isSingle ? (
                              <i className={`text-[11px] ${isSelected ? "ri-radio-button-fill text-teal-200" : "ri-checkbox-blank-circle-line text-gray-400"}`} />
                            ) : (
                              <i className={`text-[11px] ${isSelected ? "ri-checkbox-fill text-teal-200" : "ri-checkbox-blank-line text-gray-400"}`} />
                            )}
                            <span>{tag.name}</span>
                          </button>
                        );
                      })}
                      {groupTags.length === 0 && (
                        <span className="text-xs text-gray-400 italic">暂无可用标签</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {enabledGroups.length === 0 && (
                <div className="text-xs text-gray-400 p-3 bg-gray-50 rounded-lg text-center">
                  暂无已启用的标签组，请先在“标签管理”中配置并启用
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3 pt-2"><button type="button" onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700">取消</button><button type="submit" className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">保存</button></div>
        </div>
      </form>
    </div>
  );
}

interface TagGroupFormData {
  id?: string;
  name: string;
  code: string;
  selectionMode: OutfitSelectionMode;
  matchEnabled: boolean;
  enabled: boolean;
  originalMode?: OutfitSelectionMode;
}

function OutfitTagManagerModal({ groups, tags, onClose, onChanged }: { groups: AugcOutfitTagGroup[]; tags: AugcOutfitTag[]; onClose: () => void; onChanged: () => void }) {
  const [groupForm, setGroupForm] = useState<TagGroupFormData | null>(null);
  const [groupConflict, setGroupConflict] = useState<string | null>(null);
  const [groupSubmitting, setGroupSubmitting] = useState(false);

  const [inlineTagAddGroupId, setInlineTagAddGroupId] = useState<string | null>(null);
  const [inlineTagAddName, setInlineTagAddName] = useState("");
  const [inlineTagAddCode, setInlineTagAddCode] = useState("");
  const [inlineTagEditId, setInlineTagEditId] = useState<string | null>(null);
  const [inlineTagEditName, setInlineTagEditName] = useState("");
  const [inlineTagSubmitting, setInlineTagSubmitting] = useState(false);

  const request = async (path: string, method: "POST" | "PATCH" | "DELETE", body?: unknown) => {
    return apiFetch(path, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  };

  const handleSaveGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupForm) return;
    const trimmedName = groupForm.name.trim();
    if (!trimmedName) {
      toast("请填写标签组名称", "error");
      return;
    }
    const isEditing = Boolean(groupForm.id);
    const trimmedCode = groupForm.code.trim().toLowerCase();
    if (!isEditing) {
      if (!trimmedCode) {
        toast("请填写标签组编码", "error");
        return;
      }
      if (!/^[a-z0-9_]+$/.test(trimmedCode)) {
        toast("稳定编码仅限小写英文字母、数字和下划线", "error");
        return;
      }
    }

    setGroupSubmitting(true);
    try {
      if (isEditing && groupForm.id) {
        const res = await request(`/augc/outfit-tag-groups/${groupForm.id}`, "PATCH", {
          name: trimmedName,
          selectionMode: groupForm.selectionMode,
          matchEnabled: groupForm.matchEnabled,
          enabled: groupForm.enabled,
        });
        if (res.status === 409) {
          setGroupConflict("模式收敛冲突：该标签组下已有服装关联了多个标签，无法直接变更为单选模式。请先前往服装库清理相关服装的冲突标签后重试。");
          toast("保存失败：存在多标签关联冲突 (409)", "error");
          return;
        }
        if (!res.ok) {
          throw new Error(await res.text());
        }
        toast("标签组已更新", "success");
      } else {
        const res = await request("/augc/outfit-tag-groups", "POST", {
          name: trimmedName,
          code: trimmedCode,
          selectionMode: groupForm.selectionMode,
          matchEnabled: groupForm.matchEnabled,
          enabled: groupForm.enabled,
        });
        if (!res.ok) {
          throw new Error(await res.text());
        }
        toast("标签组已创建", "success");
      }
      setGroupForm(null);
      setGroupConflict(null);
      onChanged();
    } catch (error) {
      toast("操作失败：" + (error instanceof Error ? error.message : String(error)), "error");
    } finally {
      setGroupSubmitting(false);
    }
  };

  const startCreateGroup = () => {
    setGroupForm({
      name: "",
      code: "",
      selectionMode: "single",
      matchEnabled: false,
      enabled: true,
    });
    setGroupConflict(null);
  };

  const startEditGroup = (group: AugcOutfitTagGroup) => {
    setGroupForm({
      id: group.id,
      name: group.name,
      code: group.code,
      selectionMode: group.selectionMode,
      matchEnabled: group.matchEnabled ?? group.matching ?? false,
      enabled: group.enabled !== false,
      originalMode: group.selectionMode,
    });
    setGroupConflict(null);
  };

  const toggleGroupEnabled = async (group: AugcOutfitTagGroup) => {
    try {
      const res = await request(`/augc/outfit-tag-groups/${group.id}`, "PATCH", { enabled: group.enabled === false });
      if (!res.ok) throw new Error(await res.text());
      toast(group.enabled === false ? "标签组已启用" : "标签组已禁用", "success");
      onChanged();
    } catch (error) {
      toast("更新失败：" + (error instanceof Error ? error.message : String(error)), "error");
    }
  };

  const handleInlineTagAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineTagAddGroupId) return;
    const name = inlineTagAddName.trim();
    const code = inlineTagAddCode.trim().toLowerCase();
    if (!name) { toast("请填写标签名称", "error"); return; }
    if (!code) { toast("请填写标签稳定编码", "error"); return; }
    if (!/^[a-z0-9_]+$/.test(code)) {
      toast("编码仅支持小写英文字母、数字和下划线", "error");
      return;
    }
    setInlineTagSubmitting(true);
    try {
      const res = await request("/augc/outfit-tags", "POST", {
        groupId: inlineTagAddGroupId,
        name,
        code,
        enabled: true,
      });
      if (!res.ok) throw new Error(await res.text());
      toast("标签已创建", "success");
      setInlineTagAddGroupId(null);
      setInlineTagAddName("");
      setInlineTagAddCode("");
      onChanged();
    } catch (error) {
      toast("创建标签失败：" + (error instanceof Error ? error.message : String(error)), "error");
    } finally {
      setInlineTagSubmitting(false);
    }
  };

  const handleInlineTagEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineTagEditId) return;
    const name = inlineTagEditName.trim();
    if (!name) { toast("标签名称不能为空", "error"); return; }
    setInlineTagSubmitting(true);
    try {
      const res = await request(`/augc/outfit-tags/${inlineTagEditId}`, "PATCH", { name });
      if (!res.ok) throw new Error(await res.text());
      toast("标签已更新", "success");
      setInlineTagEditId(null);
      setInlineTagEditName("");
      onChanged();
    } catch (error) {
      toast("更新标签失败：" + (error instanceof Error ? error.message : String(error)), "error");
    } finally {
      setInlineTagSubmitting(false);
    }
  };

  const toggleTagEnabled = async (tag: AugcOutfitTag) => {
    try {
      const res = await request(`/augc/outfit-tags/${tag.id}`, "PATCH", { enabled: !tag.enabled });
      if (!res.ok) throw new Error(await res.text());
      toast(!tag.enabled ? "标签已启用" : "标签已禁用", "success");
      onChanged();
    } catch (error) {
      toast("更新失败：" + (error instanceof Error ? error.message : String(error)), "error");
    }
  };

  const remove = async (path: string, label: string) => {
    if (!window.confirm(`确认删除${label}？存在引用时服务会拒绝删除。`)) return;
    try {
      const res = await request(path, "DELETE");
      if (!res.ok) throw new Error(await res.text());
      toast("已删除", "success");
      onChanged();
    } catch (error) {
      toast("删除失败：" + (error instanceof Error ? error.message : String(error)), "error");
    }
  };

  const isNarrowingMode = Boolean(
    groupForm?.id && groupForm.originalMode === "multiple" && groupForm.selectionMode === "single"
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-[popIn_0.25s_ease-out]">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">标签组与标签管理</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              配置服装标签维度与可选标签。模式限制单套服装可选标签数；推荐规则保持同组 OR、跨组 AND。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <span className="text-xs text-gray-500 font-medium">{groups.length} 个标签组</span>
          {!groupForm && (
            <button
              type="button"
              onClick={startCreateGroup}
              className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors flex items-center gap-1"
            >
              <i className="ri-add-line" /> 新增标签组
            </button>
          )}
        </div>

        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          {/* Explicit Group Create / Edit Form */}
          {groupForm && (
            <form onSubmit={handleSaveGroup} className="border-2 border-teal-500/40 bg-teal-50/10 rounded-xl p-4 space-y-3.5 shadow-sm">
              <div className="flex items-center justify-between border-b border-teal-100 pb-2.5">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <i className={groupForm.id ? "ri-edit-line text-teal-600" : "ri-add-circle-line text-teal-600"} />
                  {groupForm.id ? `编辑标签组：「${groupForm.name || "未命名"}」` : "新建标签组"}
                </h4>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setGroupForm(null); setGroupConflict(null); }}
                    className="px-2.5 py-1 border border-gray-200 bg-white rounded text-xs text-gray-600 hover:bg-gray-50"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={groupSubmitting}
                    className="px-3 py-1 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-50"
                  >
                    {groupSubmitting ? "保存中..." : groupForm.id ? "保存修改" : "立即创建"}
                  </button>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    标签组名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    autoFocus
                    value={groupForm.name}
                    onChange={(e) => setGroupForm(prev => prev ? { ...prev, name: e.target.value } : null)}
                    placeholder="如：适用季节 / 拍摄场合"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center justify-between">
                    <span>稳定编码 <span className="text-red-500">*</span></span>
                    {groupForm.id && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><i className="ri-lock-line" />创建后不可修改</span>}
                  </label>
                  <input
                    required
                    disabled={Boolean(groupForm.id)}
                    value={groupForm.code}
                    onChange={(e) => setGroupForm(prev => prev ? { ...prev, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") } : null)}
                    placeholder="小写英文/数字/下划线，如 season"
                    className={`w-full px-3 py-2 border rounded-lg text-xs font-mono focus:outline-none ${
                      groupForm.id
                        ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-white border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    }`}
                  />
                </div>
              </div>

              {/* Segmented Single/Multiple Choice */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  选择模式 (单套服装打标数量限制) <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    aria-pressed={groupForm.selectionMode === "single"}
                    onClick={() => {
                      setGroupForm(prev => prev ? { ...prev, selectionMode: "single" } : null);
                      setGroupConflict(null);
                    }}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      groupForm.selectionMode === "single"
                        ? "border-teal-500 bg-teal-50/40 ring-2 ring-teal-500/20"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
                        <i className={`ri-radio-button-${groupForm.selectionMode === "single" ? "fill text-teal-600" : "line text-gray-400"}`} />
                        单选 (Single)
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">最多 1 个</span>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      每套服装在此组内只能绑定 1 个标签（互斥属性，如性别、主分类）。
                    </p>
                  </button>

                  <button
                    type="button"
                    aria-pressed={groupForm.selectionMode === "multiple"}
                    onClick={() => {
                      setGroupForm(prev => prev ? { ...prev, selectionMode: "multiple" } : null);
                      setGroupConflict(null);
                    }}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      groupForm.selectionMode === "multiple"
                        ? "border-teal-500 bg-teal-50/40 ring-2 ring-teal-500/20"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
                        <i className={`ri-checkbox-${groupForm.selectionMode === "multiple" ? "fill text-teal-600" : "blank-line text-gray-400"}`} />
                        多选 (Multiple)
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-medium">可多选</span>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      每套服装在此组内可同时绑定多个标签（复合属性，如季节、风格标签）。
                    </p>
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-gray-400">
                  模式限制单套服装可选标签数；推荐规则保持同组 OR、跨组 AND。
                </p>
              </div>

              {/* Amber warning when narrowing multiple to single */}
              {isNarrowingMode && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  <i className="ri-alert-line text-amber-600 text-sm mt-0.5 shrink-0" />
                  <div>
                    <span className="font-semibold">变更提醒：</span>
                    该标签组当前为多选模式。若已有服装关联了该组下的多个标签，变更为单选模式将被服务端拦截（HTTP 409）。切换前请确认相应服装已处理为最多 1 个标签。
                  </div>
                </div>
              )}

              {/* Toggles for Match and Enabled */}
              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50/80">
                  <div className="pr-2">
                    <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">
                      <i className="ri-filter-3-line text-teal-600" /> 参与候选匹配
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      开启后为人脸替换必选匹配维度；关闭则仅作为展示元数据
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={groupForm.matchEnabled}
                    onChange={(e) => setGroupForm(prev => prev ? { ...prev, matchEnabled: e.target.checked } : null)}
                    className="w-4 h-4 accent-teal-600 rounded cursor-pointer shrink-0"
                  />
                </label>

                <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50/80">
                  <div className="pr-2">
                    <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">
                      <i className="ri-toggle-line text-emerald-600" /> 启用此标签组
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      启用后可在服装编辑与打标中使用；禁用后不参与打标和匹配
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={groupForm.enabled}
                    onChange={(e) => setGroupForm(prev => prev ? { ...prev, enabled: e.target.checked } : null)}
                    className="w-4 h-4 accent-teal-600 rounded cursor-pointer shrink-0"
                  />
                </label>
              </div>

              {/* Conflict 409 Banner */}
              {groupConflict && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
                  <i className="ri-error-warning-line text-red-600 text-sm mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold">保存失败 (409 Conflict)：</span>
                    {groupConflict}
                  </div>
                  <button type="button" onClick={() => setGroupConflict(null)} className="text-red-400 hover:text-red-600">
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}


            </form>
          )}

          {/* Group List */}
          {groups.map((group) => {
            const isSingle = group.selectionMode === "single";
            const isMatching = Boolean(group.matchEnabled ?? group.matching ?? false);
            const isEnabled = group.enabled !== false;
            const groupTags = tags.filter((tag) => tag.groupId === group.id || tag.groupCode === group.code);

            return (
              <div key={group.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-xs space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-gray-100">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-sm text-gray-900">{group.name}</span>
                    <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      {group.code}
                    </span>

                    {/* Mode Badge */}
                    {isSingle ? (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1">
                        <i className="ri-radio-button-fill text-[10px]" /> 单选 (最多1个)
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                        <i className="ri-checkbox-fill text-[10px]" /> 多选 (可多个)
                      </span>
                    )}

                    {/* Matching / Metadata Badge */}
                    {isMatching ? (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200 inline-flex items-center gap-1">
                        <i className="ri-filter-3-line text-[10px]" /> 参与匹配
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-gray-50 text-gray-500 border border-gray-200">
                        仅元数据
                      </span>
                    )}

                    {/* Enabled / Disabled Badge */}
                    {isEnabled ? (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        已启用
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        已禁用
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        setInlineTagAddGroupId(group.id);
                        setInlineTagAddName("");
                        setInlineTagAddCode("");
                        setInlineTagEditId(null);
                      }}
                      className="px-2 py-1 text-teal-600 hover:bg-teal-50 rounded font-medium transition-colors"
                    >
                      <i className="ri-add-line mr-0.5" />新增标签
                     </button>
                    <button
                      type="button"
                      onClick={() => startEditGroup(group)}
                      className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded font-medium transition-colors"
                    >
                      <i className="ri-edit-line mr-0.5" />编辑
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleGroupEnabled(group)}
                      className="px-2 py-1 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                    >
                      {isEnabled ? "禁用" : "启用"}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(`/augc/outfit-tag-groups/${group.id}`, `标签组「${group.name}」`)}
                      className="px-2 py-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      删除
                    </button>
                  </div>
                </div>

                {/* Inline Tag Add Form */}
                {inlineTagAddGroupId === group.id && (
                  <form onSubmit={handleInlineTagAdd} className="p-3 bg-teal-50/50 border border-teal-200 rounded-lg space-y-2">
                    <div className="flex items-center justify-between text-xs font-medium text-teal-900">
                      <span>为「{group.name}」新增标签</span>
                      <button type="button" onClick={() => setInlineTagAddGroupId(null)} className="text-gray-400 hover:text-gray-600">
                        <i className="ri-close-line" />
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input
                        required
                        autoFocus
                        value={inlineTagAddName}
                        onChange={(e) => setInlineTagAddName(e.target.value)}
                        placeholder="标签名称（如：春季）"
                        className="px-2.5 py-1.5 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
                      />
                      <input
                        required
                        value={inlineTagAddCode}
                        onChange={(e) => setInlineTagAddCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                        placeholder="稳定编码（如：spring）"
                        className="px-2.5 py-1.5 bg-white border border-gray-200 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-teal-500"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setInlineTagAddGroupId(null)}
                        className="px-2.5 py-1 border border-gray-200 bg-white rounded text-xs text-gray-600 hover:bg-gray-50"
                      >
                        取消
                      </button>
                      <button
                        type="submit"
                        disabled={inlineTagSubmitting || !inlineTagAddName.trim() || !inlineTagAddCode.trim()}
                        className="px-3 py-1 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-50"
                      >
                        {inlineTagSubmitting ? "添加中..." : "确认添加"}
                      </button>
                    </div>
                  </form>
                )}

                {/* Tags List */}
                <div className="flex flex-wrap gap-2">
                  {groupTags.map((tag) => {
                    const isEditingThisTag = inlineTagEditId === tag.id;

                    if (isEditingThisTag) {
                      return (
                        <form
                          key={tag.id}
                          onSubmit={handleInlineTagEdit}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-300 text-xs shadow-xs"
                        >
                          <input
                            required
                            autoFocus
                            value={inlineTagEditName}
                            onChange={(e) => setInlineTagEditName(e.target.value)}
                            className="px-1.5 py-0.5 bg-white border border-teal-400 rounded text-xs w-24 focus:outline-none"
                          />
                          <button
                            type="submit"
                            disabled={inlineTagSubmitting || !inlineTagEditName.trim()}
                            className="text-teal-700 hover:text-teal-900 font-medium px-1"
                            title="保存名称"
                          >
                            <i className="ri-check-line" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setInlineTagEditId(null)}
                            className="text-gray-400 hover:text-gray-600 px-1"
                            title="取消"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </form>
                      );
                    }

                    return (
                      <span
                        key={tag.id}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-colors ${
                          tag.enabled
                            ? "bg-gray-100 text-gray-700 border border-gray-200/80"
                            : "bg-gray-50 text-gray-400 border border-dashed border-gray-200"
                        }`}
                      >
                        <span>{tag.name}</span>
                        <span className="text-[10px] font-mono text-gray-400">({tag.code})</span>
                        <button
                          type="button"
                          onClick={() => {
                            setInlineTagEditId(tag.id);
                            setInlineTagEditName(tag.name);
                            setInlineTagAddGroupId(null);
                          }}
                          className="text-gray-400 hover:text-teal-600 ml-0.5"
                          title="重命名标签"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleTagEnabled(tag)}
                          className="text-gray-400 hover:text-gray-600"
                          title={tag.enabled ? "禁用标签" : "启用标签"}
                        >
                          {tag.enabled ? <i className="ri-eye-line text-emerald-600" /> : <i className="ri-eye-off-line text-amber-500" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(`/augc/outfit-tags/${tag.id}`, `标签「${tag.name}」`)}
                          className="text-gray-400 hover:text-red-500 ml-0.5"
                          title="删除标签"
                        >
                          <i className="ri-close-line" />
                        </button>
                      </span>
                    );
                  })}
                  {groupTags.length === 0 && (
                    <span className="text-xs text-gray-400 italic">暂无标签，点击右上角“新增标签”添加</span>
                  )}
                </div>
              </div>
            );
          })}

          {groups.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-xs">
              暂无标签组，点击上方“新增标签组”开始创建
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
interface AugcArchiveError {
  code: string;
  message: string;
}

interface AugcItemError {
  field: string;
  code: string;
  message: string;
}

interface AugcImportItemResult {
  index: number;
  name: string;
  errors?: AugcItemError[];
}

interface AugcBulkImportResponse {
  valid: boolean;
  archiveErrors?: AugcArchiveError[];
  items?: AugcImportItemResult[];
}

function OutfitBulkImportModal({
  open,
  groups,
  tags,
  onClose,
  onImported,
}: {
  open: boolean;
  groups: AugcOutfitTagGroup[];
  tags: AugcOutfitTag[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!open) return null;

  const enabledGroups = groups.filter((group) => group.enabled !== false);
  const matchingGroups = enabledGroups.filter((group) => group.matchEnabled ?? group.matching ?? false);
  const tagForGroup = (group: AugcOutfitTagGroup) => tags.filter((tag) => tag.enabled && (tag.groupId === group.id || tag.groupCode === group.code));
  const toggleTag = (group: AugcOutfitTagGroup, id: string) => setTagIds((current) => {
    const groupIds = new Set(tagForGroup(group).map((tag) => tag.id));
    if (group.selectionMode === "single") return [...current.filter((item) => !groupIds.has(item)), ...(current.includes(id) ? [] : [id])];
    return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
  });
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (fl.length > 0) {
      setFiles((prev) => [...prev, ...fl]);
    }
    if (e.target) e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (submitting) return;
    const fl = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    if (fl.length > 0) {
      setFiles((prev) => [...prev, ...fl]);
    }
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearFiles = () => {
    setFiles([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missingGroup = matchingGroups.find((group) => !tagIds.some((id) => tagForGroup(group).some((tag) => tag.id === id)));
    if (missingGroup) { toast(`请选择「${missingGroup.name}」标签`, "error"); return; }
    if (files.length === 0) {
      toast("请选择至少一张图片", "error");
      return;
    }

    setSubmitting(true);
    try {
      const tagsMap: Record<string, string[]> = {};
      for (const group of enabledGroups) {
        const selectedCodes = tagForGroup(group)
          .filter((tag) => tagIds.includes(tag.id))
          .map((tag) => tag.code);
        if (selectedCodes.length > 0) {
          tagsMap[group.code] = selectedCodes;
        }
      }
      const effectiveConstraints = Object.fromEntries(
        matchingGroups.map((group) => [group.code, "include"]),
      );
      const formData = new FormData();
      formData.append("config", JSON.stringify({ tags: tagsMap, constraints: effectiveConstraints }));
      for (const f of files) {
        formData.append("files", f);
      }

      const res = await apiFetch("/augc/outfits/batch-import", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let errText = "";
        try {
          const errData = await res.json();
          if (errData.archiveErrors && errData.archiveErrors.length > 0) {
            errText = errData.archiveErrors.map((e: { message: string }) => e.message).join("；");
          } else if (errData.items && errData.items.length > 0) {
            const itemErrs = errData.items
              .flatMap((item: { errors?: { message: string }[] }) => item.errors || [])
              .map((e: { message: string }) => e.message);
            errText = itemErrs.length > 0 ? itemErrs.join("；") : "服装数据未通过校验";
          } else {
            errText = errData.message || res.statusText;
          }
        } catch {
          errText = await res.text().catch(() => "导入请求失败");
        }
        throw new Error(errText || "导入失败");
      }

      const out = (await res.json().catch(() => ({}))) as { count?: number };
      toast(`成功导入 ${out.count ?? files.length} 套服装`, "success");
      onImported();
      onClose();
    } catch (err) {
      toast("批量导入失败：" + (err instanceof Error ? err.message : String(err)), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !submitting && onClose()} />
      <div className="relative bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-[popIn_0.25s_ease-out]">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">批量导入服装</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1">
          <div className="p-5 space-y-5 overflow-y-auto flex-1">
            {/* Shared Tags */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">共享服装标签</h4>
              <div className="space-y-3">
                {enabledGroups.map((group) => {
                  const isSingle = group.selectionMode === "single";
                  const isMatching = Boolean(group.matchEnabled ?? group.matching ?? false);
                  const groupTags = tagForGroup(group);
                  return (
                    <div key={group.id} className="p-3 bg-gray-50/70 border border-gray-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-900">{group.name}</span>
                        {isMatching ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                            <span className="text-rose-600 font-bold">*</span>必选匹配
                          </span>
                        ) : null}
                        {isSingle ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            单选
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                            多选
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {groupTags.map((tag) => {
                          const isSelected = tagIds.includes(tag.id);
                          return (
                            <button
                              type="button"
                              key={tag.id}
                              onClick={() => toggleTag(group, tag.id)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                isSelected
                                  ? isSingle
                                    ? "bg-teal-600 text-white shadow-sm ring-2 ring-teal-600/30"
                                    : "bg-teal-600 text-white shadow-sm"
                                  : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              {isSingle ? (
                                <i className={`text-[11px] ${isSelected ? "ri-radio-button-fill text-teal-200" : "ri-checkbox-blank-circle-line text-gray-400"}`} />
                              ) : (
                                <i className={`text-[11px] ${isSelected ? "ri-checkbox-fill text-teal-200" : "ri-checkbox-blank-line text-gray-400"}`} />
                              )}
                              <span>{tag.name}</span>
                            </button>
                          );
                        })}
                        {groupTags.length === 0 && (
                          <span className="text-xs text-gray-400 italic">暂无可用标签</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {enabledGroups.length === 0 && (
                  <div className="text-xs text-gray-400 p-3 bg-gray-50 rounded-lg text-center">
                    暂无已启用的标签组，请先在“标签管理”中配置并启用
                  </div>
                )}
              </div>
            </div>

            {/* Multi Image Select */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">选择图片（可多选）</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 transition-colors"
              >
                <i className="ri-upload-2-line text-2xl text-gray-400" />
                <span className="text-sm text-gray-500">点击或拖入多张服装图片</span>
                <span className="text-xs text-gray-400">服装名称将自动取自文件名（去除扩展名）</span>
              </div>
            </div>

            {/* Selected Files List */}
            {files.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-3 max-h-56 overflow-y-auto space-y-1.5 bg-gray-50/50">
                <div className="flex items-center justify-between px-1 pb-1 text-xs text-gray-500 font-medium">
                  <span>已选 {files.length} 张图片</span>
                  <button type="button" onClick={clearFiles} className="text-gray-500 hover:text-red-600 transition-colors">
                    清空
                  </button>
                </div>
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-white border border-gray-200/80 rounded text-xs">
                    <span className="truncate flex-1 font-medium text-gray-800">{f.name}</span>
                    <span className="text-gray-400 mx-2 text-[11px]">{(f.size / 1024).toFixed(0)} KB</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-0.5"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-white transition-colors disabled:opacity-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting || files.length === 0}
              className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  导入中...
                </>
              ) : (
                <>
                  <i className="ri-upload-2-line" />
                  导入 {files.length} 张
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OutfitDetailDrawer({
  outfit,
  groups,
  tags,
  onClose,
  onEdit,
  onDelete,
  onToggleEnabled,
}: {
  outfit: AugcOutfit | null;
  groups: AugcOutfitTagGroup[];
  tags: AugcOutfitTag[];
  onClose: () => void;
  onEdit: (outfit: AugcOutfit) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (outfit: AugcOutfit) => void;
}) {
  if (!outfit) return null;

  const assignedTagIds = new Set(
    outfit.tagAssignments?.map((t) => t.id) ||
    tags.filter((t) => outfit.tagCodes?.includes(t.code)).map((t) => t.id)
  );
  const assignedTagCodes = new Set(
    outfit.tagCodes || outfit.tagAssignments?.map((t) => t.code) || []
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto animate-[slideIn_0.3s_ease-out]">
        <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        <div className="relative h-80 overflow-hidden bg-slate-100">
          {outfit.displayImageUrl ? (
            <img
              src={outfit.displayImageUrl}
              alt={outfit.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <i className="ri-shirt-line text-5xl" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
          <button
            onClick={onClose}
            aria-label="关闭详情"
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full hover:bg-white transition-colors pointer-events-auto"
          >
            <i className="ri-close-line text-gray-700" />
          </button>
          <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  outfit.enabled
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {outfit.enabled ? "已启用" : "已禁用"}
              </span>
            </div>
            <h2 className="text-xl font-bold text-white">{outfit.name}</h2>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {outfit.createdAt && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                创建时间
              </h3>
              <p className="text-sm text-gray-700">{outfit.createdAt}</p>
            </div>
          )}

          {/* Grouped Tags */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              服装标签 (按标签组)
            </h3>
            <div className="space-y-3">
              {groups.map((group) => {
                const groupTags = tags.filter(
                  (t) =>
                    (t.groupId === group.id || t.groupCode === group.code) &&
                    (assignedTagIds.has(t.id) || assignedTagCodes.has(t.code))
                );
                return (
                  <div key={group.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-gray-700">
                        {group.name}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {group.selectionMode === "single" ? "单选" : "多选"}
                      </span>
                    </div>
                    {groupTags.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {groupTags.map((tag) => (
                          <span
                            key={tag.id}
                            className="px-2 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-100 text-xs font-medium"
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">未设置标签</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>


          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">启用状态</span>
              <label
                className="relative inline-flex items-center cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={outfit.enabled}
                  aria-label="启用此服装"
                  className="sr-only peer"
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleEnabled(outfit);
                  }}
                />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(outfit);
                }}
                aria-label="编辑服装"
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-edit-line mr-1" />编辑
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(outfit.id);
                }}
                aria-label="删除服装"
                className="px-4 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-1" />删除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OutfitCard({
  outfit,
  groups,
  tags,
  onOpenDrawer,
  onEdit,
  onDelete,
  onToggleEnabled,
  batchMode,
  selected,
  onToggleSelect,
}: {
  outfit: AugcOutfit;
  groups: AugcOutfitTagGroup[];
  tags: AugcOutfitTag[];
  onOpenDrawer: (o: AugcOutfit) => void;
  onEdit: (o: AugcOutfit) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (o: AugcOutfit) => void;
  batchMode: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
}) {
  const statusBadgeClass = outfit.enabled
    ? "bg-emerald-100 text-emerald-700"
    : "bg-gray-100 text-gray-600";
  const statusText = outfit.enabled ? "已启用" : "已禁用";

  return (
    <div
      onClick={() => (batchMode ? onToggleSelect(outfit.id) : onOpenDrawer(outfit))}
      className={`group cursor-pointer bg-white rounded-xl border border-gray-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${
        selected ? "ring-2 ring-teal-500" : ""
      }`}
    >
      <div
        className="relative w-full h-56 overflow-hidden bg-gradient-to-b from-slate-50 to-slate-200"
      >
        {outfit.displayImageUrl ? (
          <img
            src={outfit.displayImageUrl}
            alt={outfit.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <i className="ri-shirt-line text-4xl" />
          </div>
        )}
        {batchMode && (
          <div
            className="absolute top-3 left-3 z-10 pointer-events-auto"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(outfit.id);
            }}
          >
            <div
              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                selected
                  ? "bg-teal-600 border-teal-600"
                  : "bg-white/80 border-white/80 hover:bg-white"
              }`}
            >
              {selected && <i className="ri-check-line text-white text-xs" />}
            </div>
          </div>
        )}
        <span
          className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-medium pointer-events-none ${statusBadgeClass}`}
        >
          {statusText}
        </span>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-300 flex items-end justify-center pb-3 gap-2 pointer-events-none">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenDrawer(outfit);
            }}
            aria-label="查看服装详情"
            className="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full bg-white/95 text-xs font-medium text-gray-800 flex items-center gap-1 hover:bg-white"
          >
            <i className="ri-eye-line" /> 详情
          </button>
        </div>
      </div>
      <div className="px-4 py-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900 truncate flex-1">
            {outfit.name}
          </h3>
        </div>
        <div className="flex flex-wrap gap-1 mt-2 min-h-5">
          {outfit.tagAssignments && outfit.tagAssignments.length > 0 ? (
            outfit.tagAssignments.map((tag) => (
              <span
                key={tag.id}
                className="px-1.5 py-0.5 bg-teal-50 text-teal-700 border border-teal-100 rounded text-[10px]"
              >
                {tag.name}
              </span>
            ))
          ) : outfit.tagCodes.length > 0 ? (
            outfit.tagCodes.map((code) => {
              const tagName = tags.find((t) => t.code === code)?.name || code;
              return (
                <span
                  key={code}
                  className="px-1.5 py-0.5 bg-teal-50 text-teal-700 border border-teal-100 rounded text-[10px]"
                >
                  {tagName}
                </span>
              );
            })
          ) : (
            <span className="text-[10px] text-gray-400">无标签</span>
          )}
        </div>
        <div className="flex items-center justify-end gap-1.5 mt-3 pt-2 border-t border-gray-50">
          <label
            className="relative inline-flex items-center cursor-pointer mr-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={outfit.enabled}
              aria-label="启用服装"
              className="sr-only peer"
              onChange={(e) => {
                e.stopPropagation();
                onToggleEnabled(outfit);
              }}
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600" />
          </label>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(outfit);
            }}
            aria-label="编辑服装"
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
          >
            <i className="ri-edit-line text-sm" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(outfit.id);
            }}
            aria-label="删除服装"
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

function OutfitLibrary({ initialOutfits, groups, tags, reloadOutfits, reloadTagData }: { initialOutfits: AugcOutfit[]; groups: AugcOutfitTagGroup[]; tags: AugcOutfitTag[]; reloadOutfits: () => void; reloadTagData: () => void }) {
  const [outfits, setOutfits] = useState(initialOutfits.map(normalizeOutfit));
  const [query, setQuery] = useState("");
  const [facets, setFacets] = useState<Record<string, string[]>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [editing, setEditing] = useState<AugcOutfit | null | undefined>(undefined);
  const [detailOutfit, setDetailOutfit] = useState<AugcOutfit | null>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);

  const loadAllOutfits = useCallback(async () => {
    const all: AugcOutfit[] = [];
    let page = 1;
    let total = 0;
    do {
      const response = await apiFetch(`/augc/outfits?page=${page}&pageSize=100`);
      if (!response.ok) throw new Error(await response.text());
      const result: AugcOutfitListResponse = await response.json();
      all.push(...result.items);
      total = result.total;
      page += 1;
    } while (all.length < total);
    setOutfits(all.map(normalizeOutfit));
  }, []);

  const [showTagManager, setShowTagManager] = useState(false);
  useEffect(() => { setOutfits(initialOutfits.map(normalizeOutfit)); void loadAllOutfits().catch(() => undefined); }, [initialOutfits, loadAllOutfits]);

  const tagsFor = (group: AugcOutfitTagGroup) => tags.filter((tag) => tag.enabled && (tag.groupId === group.id || tag.groupCode === group.code));

  const filtered = useMemo(() => outfits.filter((outfit) => (outfit.name.includes(query) || outfit.tagAssignments?.some((tag) => tag.name.includes(query))) && Object.entries(facets).every(([groupCode, codes]) => !codes.length || outfit.tagAssignments?.some((tag) => tag.groupCode === groupCode && codes.includes(tag.code)))), [outfits, query, facets]);

  const update = async (path: string, body?: unknown) => { const response = await apiFetch(path, { method: "POST", headers: body ? { "Content-Type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined }); if (!response.ok) throw new Error(await response.text()); };

  const toggleFacet = (groupCode: string, code: string) => setFacets((current) => ({ ...current, [groupCode]: current[groupCode]?.includes(code) ? current[groupCode].filter((item) => item !== code) : [...(current[groupCode] || []), code] }));

  const toggleEnabled = async (outfit: AugcOutfit) => {
    try {
      const response = await apiFetch(`/augc/outfits/${outfit.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled: !outfit.enabled }) });
      if (!response.ok) throw new Error(await response.text());
      setOutfits((current) => current.map((item) => item.id === outfit.id ? { ...item, enabled: !item.enabled } : item));
      setDetailOutfit((current) => current?.id === outfit.id ? { ...current, enabled: !current.enabled } : current);
      toast(!outfit.enabled ? "服装已启用" : "服装已禁用", "success");
    } catch (error) {
      toast("更新失败：" + String(error), "error");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("确认删除该服装？")) return;
    try {
      const response = await apiFetch(`/augc/outfits/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await response.text());
      setOutfits((current) => current.filter((item) => item.id !== id));
      setSelected((current) => {
        if (!current.has(id)) return current;
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      if (detailOutfit?.id === id) setDetailOutfit(null);
      toast("服装已删除", "success");
    } catch (error) {
      toast("删除失败：" + String(error), "error");
    }
  };

  const bulk = async (action: "enable" | "delete") => {
    const ids = [...selected];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`确认删除选中的 ${ids.length} 个服装？`)) return;
    try {
      await update(`/augc/outfits/bulk-${action}`, action === "enable" ? { ids, enabled: true } : { ids });
      setOutfits((current) => action === "delete" ? current.filter((item) => !selected.has(item.id)) : current.map((item) => selected.has(item.id) ? { ...item, enabled: true } : item));
      if (detailOutfit && selected.has(detailOutfit.id)) {
        if (action === "delete") setDetailOutfit(null);
        else setDetailOutfit((current) => current ? { ...current, enabled: true } : null);
      }
      setSelected(new Set());
      setBatchMode(false);
      toast(action === "delete" ? "已删除服装" : "已启用服装", "success");
    } catch (error) {
      toast("批量操作失败：" + String(error), "error");
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">服装库</h2>
          <p className="text-xs text-gray-500 mt-1">展示图仅用于后台识别与管理，不会作为模型输入。</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowTagManager(true)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700"><i className="ri-price-tag-3-line mr-1" />标签管理</button>
          <button onClick={() => setShowBulkImport(true)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700"><i className="ri-upload-2-line mr-1" />批量导入</button>
          <button onClick={() => setEditing(null)} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium"><i className="ri-add-line mr-1" />新建服装</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索服装或标签" className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
        </div>
        {groups.map((group) => (
          <div className="flex items-center gap-2 flex-wrap" key={group.id}>
            <span className="text-xs text-gray-500 w-16">{group.name}:</span>
            {tagsFor(group).map((tag) => (
              <button key={tag.id} onClick={() => toggleFacet(group.code, tag.code)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${facets[group.code]?.includes(tag.code) ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{tag.name}</button>
            ))}
          </div>
        ))}
      </div>

      {batchMode ? (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-teal-800">已选 {selected.size} 项</span>
            <button onClick={() => setSelected(new Set(filtered.map((o) => o.id)))} className="text-xs text-teal-600 hover:text-teal-800 font-medium whitespace-nowrap">全选</button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap">清空</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => bulk("enable")} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap flex items-center gap-1"><i className="ri-check-line" /> 批量启用</button>
            <button onClick={() => bulk("delete")} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors whitespace-nowrap flex items-center gap-1"><i className="ri-delete-bin-line" /> 批量删除</button>
            <button onClick={() => { setBatchMode(false); setSelected(new Set()); }} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <button onClick={() => setBatchMode(true)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-1">
            <i className="ri-checkbox-multiple-line" /> 批量操作
          </button>
        </div>
      )}

      {editing !== undefined && <OutfitEditorModal outfit={editing} groups={groups} tags={tags} onClose={() => setEditing(undefined)} onSaved={() => { void loadAllOutfits(); reloadOutfits(); }} />}

      <OutfitDetailDrawer
        outfit={detailOutfit}
        groups={groups}
        tags={tags}
        onClose={() => setDetailOutfit(null)}
        onEdit={(o) => {
          setDetailOutfit(null);
          setEditing(o);
        }}
        onDelete={(id) => {
          void remove(id);
        }}
        onToggleEnabled={toggleEnabled}
      />

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <i className="ri-shirt-line text-4xl mb-3 block" />
          <p className="text-sm">暂无符合条件的服装</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((outfit) => (
            <OutfitCard
              key={outfit.id}
              outfit={outfit}
              groups={groups}
              tags={tags}
              onOpenDrawer={setDetailOutfit}
              onEdit={(o) => setEditing(o)}
              onDelete={(id) => void remove(id)}
              onToggleEnabled={toggleEnabled}
              batchMode={batchMode}
              selected={selected.has(outfit.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      {showTagManager && <OutfitTagManagerModal groups={groups} tags={tags} onClose={() => setShowTagManager(false)} onChanged={reloadTagData} />}
      {showBulkImport && <OutfitBulkImportModal open={showBulkImport} groups={groups} tags={tags} onClose={() => setShowBulkImport(false)} onImported={() => { void loadAllOutfits(); reloadOutfits(); }} />}
    </div>
  );
}

/* ═══════════════════  MAIN PAGE  ═══════════════════ */
function FaceswapPage() {
  // Live data from the Go API, with bundled mock as fallback while loading /
  // if the backend is unreachable.
  const { data: augcStats } = useApi<typeof mockAugcStats>("/data/augcStats", mockAugcStats);
  const { data: styleCollectionsSeed } = useApi<typeof mockStyleCollections>("/data/styleCollections", mockStyleCollections);
  const { data: styleTemplates } = useApi<typeof mockStyleTemplates>("/data/styleTemplates", mockStyleTemplates);
  const { data: pureSwapTemplates } = useApi<typeof mockPureSwapTemplates>("/data/pureSwapTemplates", mockPureSwapTemplates);
  const { data: fsStats } = useApi<FaceswapStats>("/faceswap/stats", FALLBACK_FACESWAP_STATS);
  const { data: realTrend } = useApi<FaceswapTrend>("/faceswap/trend", {
    today: mockDailyTrendDataToday,
    d7: mockDailyTrendData7d,
    d30: mockDailyTrendData30d,
  });

  const { data: mockUserPhotos } = useApi<typeof mockUserPhotosSeed>("/data/mockUserPhotos", mockUserPhotosSeed);
  const { data: styleRankDataToday } = useApi<typeof mockStyleRankDataToday>("/data/styleRankDataToday", mockStyleRankDataToday);
  const { data: styleRankData7d } = useApi<typeof mockStyleRankData7d>("/data/styleRankData7d", mockStyleRankData7d);
  const { data: styleRankData30d } = useApi<typeof mockStyleRankData30d>("/data/styleRankData30d", mockStyleRankData30d);
  const { data: sceneHeatDataToday } = useApi<typeof mockSceneHeatDataToday>("/data/sceneHeatDataToday", mockSceneHeatDataToday);
  const { data: sceneHeatData7d } = useApi<typeof mockSceneHeatData7d>("/data/sceneHeatData7d", mockSceneHeatData7d);
  const { data: sceneHeatData30d } = useApi<typeof mockSceneHeatData30d>("/data/sceneHeatData30d", mockSceneHeatData30d);
  const { data: dailyTrendDataToday } = useApi<typeof mockDailyTrendDataToday>("/data/dailyTrendDataToday", mockDailyTrendDataToday);
  const { data: dailyTrendData7d } = useApi<typeof mockDailyTrendData7d>("/data/dailyTrendData7d", mockDailyTrendData7d);
  const { data: dailyTrendData30d } = useApi<typeof mockDailyTrendData30d>("/data/dailyTrendData30d", mockDailyTrendData30d);
  const { data: outfitList, reload: reloadOutfits } = useApi<AugcOutfitListResponse>("/augc/outfits?page=1&pageSize=100", OUTFIT_FALLBACK_LIST);
  const { data: outfitTagGroups, reload: reloadOutfitTagGroups } = useApi<AugcOutfitTagGroup[]>("/augc/outfit-tag-groups", OUTFIT_FALLBACK_GROUPS);
  const { data: outfitTags, reload: reloadOutfitTags } = useApi<AugcOutfitTag[]>("/augc/outfit-tags", OUTFIT_FALLBACK_TAGS);
  const { data: actionPromptGroups, reload: reloadActionPromptGroups } = useApi<ActionPromptTagGroup[]>("/faceswap/action-prompt-tag-groups", []);
  const { data: actionPromptTags, reload: reloadActionPromptTags } = useApi<ActionPromptTag[]>("/faceswap/action-prompt-tags", []);
  const { prompts: actionPrompts, reload: reloadActionPrompts } = useActionPromptsCatalog();

  type AugcTab = "style" | "pure" | "outfit" | "action" | "history" | "beauty";
  const [activeTab, setActiveTab] = useState<AugcTab>("style");
  const beautyLeaveHandlerRef = useRef<BeautyPresetLeaveHandler | null>(null);
  const pendingBeautyTabRef = useRef<AugcTab | null>(null);
  const pendingBeautyLeaveRef = useRef<Promise<boolean> | null>(null);
  const registerBeautyLeaveHandler = useCallback((handler: BeautyPresetLeaveHandler | null) => {
    beautyLeaveHandlerRef.current = handler;
  }, []);
  const requestTabChange = useCallback((tab: AugcTab) => {
    if (tab === activeTab) return;
    if (activeTab === "beauty") {
      const requestLeave = beautyLeaveHandlerRef.current;
      if (requestLeave) {
        pendingBeautyTabRef.current = tab;
        if (!pendingBeautyLeaveRef.current) {
          const pendingLeave = requestLeave();
          pendingBeautyLeaveRef.current = pendingLeave;
          void pendingLeave.then((allowed) => {
            pendingBeautyLeaveRef.current = null;
            const nextTab = pendingBeautyTabRef.current;
            pendingBeautyTabRef.current = null;
            if (allowed && nextTab) setActiveTab(nextTab);
          });
        }
        return;
      }
    }
    setActiveTab(tab);
  }, [activeTab]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [selectedSubsubcategory, setSelectedSubsubcategory] = useState<string | null>(null);
  const [selectedSceneType, setSelectedSceneType] = useState<string>("all");
  const [showBatchUpload, setShowBatchUpload] = useState(false);
  const [previewStyle, setPreviewStyle] = useState<ManagedStyleTemplate | null>(null);
  const [previewPure, setPreviewPure] = useState<PureSwapTemplate | null>(null);
  const [editingStyleTemplate, setEditingStyleTemplate] = useState<ManagedStyleTemplate | null>(null);
  const [editingPureTemplate, setEditingPureTemplate] = useState<PureSwapTemplate | null>(null);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showFaceSwap, setShowFaceSwap] = useState(false);
  const [faceSwapTemplate, setFaceSwapTemplate] = useState<PureSwapTemplate | null>(null);
  const [previewEffectTemplate, setPreviewEffectTemplate] = useState<StyleTemplate | PureSwapTemplate | null>(null);
  const [timeRange, setTimeRange] = useState<"today" | "7d" | "30d">("30d");
  const [rankTab, setRankTab] = useState<"style" | "scene">("style");
  const [batchMode, setBatchMode] = useState(false);
  const [selectedStyleIds, setSelectedStyleIds] = useState<Set<string>>(new Set());
  const [selectedPureIds, setSelectedPureIds] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<ManagedStyleTemplate[]>(styleTemplates);

  // Real IP templates (faceswap_templates table, categories 金梅导游/鲟侠船长)
  // replace the seeded style mocks once loaded; collections derive from the
  // actual categories so the strip always matches the data.
  const { data: realIPTemplates, reload: reloadIPTemplates } = useApi<{
    id: string; name: string; category: string; subcategory?: string; subsubcategory?: string; image: string; enabled: boolean;
    generateCount: number; complianceStatus: string; description: string;
    style?: string; scene?: string; prompt?: string; generationProvider?: GenerationProvider; generation_provider?: GenerationProvider;
    outfitType?: OutfitTypeTag | null;
    outfitFilterTags?: OutfitTypeTag[];
    supplementalImages?: SupplementalImage[];
    actionPrompts?: ActionPromptRelation[];
  }[]>("/faceswap/templates", []);
  // Editable two-level category tree (faceswap_categories). Manage via CategoriesManagerModal.
  const { data: catTree, reload: reloadCatTree } = useApi<FSCategoryNode[]>("/faceswap/categories", []);
  const topCategories = useMemo(() => catTree.map((t) => t.name), [catTree]);
  const subMap = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const t of catTree) m[t.name] = (t.children || []).map((c) => c.name);
    return m;
  }, [catTree]);
  const sub2Map = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const t of catTree) {
      for (const sub of (t.children || [])) {
        m[sub2Key(t.name, sub.name)] = (sub.children || []).map((c) => c.name);
      }
    }
    return m;
  }, [catTree]);
  const [showCategoriesManager, setShowCategoriesManager] = useState(false);
  useEffect(() => {
    setTemplates(realIPTemplates.map((t) => ({
      id: t.id, name: t.name, styleId: t.category, styleName: t.category,
      subcategory: t.subcategory || "",
      subsubcategory: t.subsubcategory || "",
      image: t.image, enabled: t.enabled, generateCount: t.generateCount || 0,
      todayCount: 0,
      complianceStatus: t.complianceStatus === "approved" ? "approved" : "pending",
      generationProvider: (t.generationProvider || t.generation_provider) === "wan" ? "wan" : "facefusion",
      description: t.description || "",
      style: t.style || defaultFaceswapStyle(t.category),
      scene: t.scene || "03",
      prompt: t.prompt || "",
      outfitType: t.outfitType || null,
      outfitFilterTags: t.outfitFilterTags || [],
      supplementalImages: t.supplementalImages || [],
      actionPrompts: t.actionPrompts || [],
    })));
    // eslint-disable-next-line
  }, [realIPTemplates]);
  const [pureTemplates, setPureTemplates] = useState(pureSwapTemplates);

  // 二级分类：顶级即 CATEGORY_TREE 的 key（男/女/儿童/卡通），
  // subcategory 即 value 数组（船长/其他/金梅导游/鲟侠船长）。
  // 用于左侧导航与统计卡。
  const categoryStats = useMemo(() => {
    return topCategories.map((cat) => {
      const list = templates.filter((t) => t.styleId === cat);
      const subStats = (subMap[cat] || []).map((sub) => {
        const sub2List = sub2Map[sub2Key(cat, sub)] || [];
        const sub2Stats = sub2List.map((sub2) => ({
          sub2,
          count: templates.filter((t) => t.styleId === cat && (t.subcategory || "") === sub && (t.subsubcategory || "") === sub2).length,
        }));
        return {
          sub,
          count: templates.filter((t) => t.styleId === cat && (t.subcategory || "") === sub).length,
          sub2Stats,
        };
      });
      return {
        cat,
        cover: list[0]?.image || "",
        count: list.length,
        generateCount: list.reduce((n, t) => n + (t.generateCount || 0), 0),
        subStats,
      };
    });
  }, [templates, topCategories, subMap, sub2Map]);

  const filteredStyleTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesSearch = searchQuery === "" || t.name.includes(searchQuery);
      const matchesCat = selectedCategory === null || t.styleId === selectedCategory;
      const matchesSub = selectedSubcategory === null || (t.subcategory || "") === selectedSubcategory;
      const matchesSub2 = selectedSubsubcategory === null || (t.subsubcategory || "") === selectedSubsubcategory;
      return matchesSearch && matchesCat && matchesSub && matchesSub2;
    });
  }, [templates, searchQuery, selectedCategory, selectedSubcategory, selectedSubsubcategory]);

  const filteredPureTemplates = useMemo(() => {
    return pureTemplates.filter((t) => {
      const matchesSearch = searchQuery === "" || t.name.includes(searchQuery);
      const matchesScene = selectedSceneType === "all" || t.sceneType === selectedSceneType;
      return matchesSearch && matchesScene;
    });
  }, [pureTemplates, searchQuery, selectedSceneType]);

  const sceneTypes = useMemo(() => Array.from(new Set(pureTemplates.map((t) => t.sceneType))), [pureTemplates]);
  const resetFilters = useCallback(() => { setSearchQuery(""); setSelectedCategory(null); setSelectedSubcategory(null); setSelectedSubsubcategory(null); setSelectedSceneType("all"); }, []);
  const hasFilters = searchQuery !== "" || selectedCategory !== null || selectedSubcategory !== null || selectedSubsubcategory !== null || selectedSceneType !== "all";

  const toggleStyleSelect = useCallback((id: string) => {
    setSelectedStyleIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const togglePureSelect = useCallback((id: string) => {
    setSelectedPureIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const clearSelection = useCallback(() => { setSelectedStyleIds(new Set()); setSelectedPureIds(new Set()); }, []);

  const updateStyleEnabledLocal = useCallback((id: string, enabled: boolean) => {
    setTemplates((prev) => prev.map((t) => t.id === id ? { ...t, enabled } : t));
    setPreviewStyle((current) => current?.id === id ? { ...current, enabled } : current);
  }, []);

  const updatePureEnabledLocal = useCallback((id: string, enabled: boolean) => {
    setPureTemplates((prev) => prev.map((t) => t.id === id ? { ...t, enabled } : t));
    setPreviewPure((current) => current?.id === id ? { ...current, enabled } : current);
  }, []);

  const toggleStyleEnabled = useCallback(async (template: StyleTemplate) => {
    const previousEnabled = template.enabled;
    const nextEnabled = !previousEnabled;
    updateStyleEnabledLocal(template.id, nextEnabled);
    if (String(template.id).startsWith("pure-")) {
      toast(nextEnabled ? "模板已启用" : "模板已禁用", "success");
      return;
    }
    try {
      const res = await apiFetch(`/faceswap/templates/${template.id}/toggle`, { method: "POST" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json() as { id: string; enabled: boolean };
      updateStyleEnabledLocal(template.id, data.enabled);
      toast(data.enabled ? "模板已启用" : "模板已禁用", "success");
      reloadIPTemplates();
    } catch (e) {
      updateStyleEnabledLocal(template.id, previousEnabled);
      toast("更新启用状态失败：" + (e instanceof Error ? e.message : String(e)), "error");
      reloadIPTemplates();
    }
  }, [reloadIPTemplates, updateStyleEnabledLocal]);

  const togglePureEnabled = useCallback((template: PureSwapTemplate) => {
    const nextEnabled = !template.enabled;
    updatePureEnabledLocal(template.id, nextEnabled);
    toast(nextEnabled ? "模板已启用" : "模板已禁用", "success");
  }, [updatePureEnabledLocal]);

  const batchEnable = useCallback(async (enable: boolean) => {
    if (activeTab === "style") {
      const selected = templates.filter((t) => selectedStyleIds.has(t.id) && t.enabled !== enable);
      const previous = templates;
      setTemplates((prev) => prev.map((t) => selectedStyleIds.has(t.id) ? { ...t, enabled: enable } : t));
      setPreviewStyle((current) => current && selectedStyleIds.has(current.id) ? { ...current, enabled: enable } : current);
      setSelectedStyleIds(new Set());
      setBatchMode(false);
      const realSelected = selected.filter((t) => !String(t.id).startsWith("pure-"));
      if (realSelected.length > 0) {
        try {
          await Promise.all(realSelected.map(async (t) => {
            const res = await apiFetch(`/faceswap/templates/${t.id}/toggle`, { method: "POST" });
            if (!res.ok) throw new Error(await res.text());
            return res.json();
          }));
          toast(`已${enable ? "启用" : "禁用"} ${selected.length} 个模板`, "success");
          reloadIPTemplates();
        } catch (e) {
          setTemplates(previous);
          setPreviewStyle((current) => current ? previous.find((t) => t.id === current.id) || current : current);
          toast("批量更新启用状态失败：" + (e instanceof Error ? e.message : String(e)), "error");
          reloadIPTemplates();
        }
      } else if (selected.length > 0) {
        toast(`已${enable ? "启用" : "禁用"} ${selected.length} 个模板`, "success");
      }
    } else if (activeTab === "pure") {
      const selected = pureTemplates.filter((t) => selectedPureIds.has(t.id) && t.enabled !== enable);
      setPureTemplates((prev) => prev.map((t) => selectedPureIds.has(t.id) ? { ...t, enabled: enable } : t));
      setPreviewPure((current) => current && selectedPureIds.has(current.id) ? { ...current, enabled: enable } : current);
      setSelectedPureIds(new Set());
      setBatchMode(false);
      if (selected.length > 0) toast(`已${enable ? "启用" : "禁用"} ${selected.length} 个模板`, "success");
    }
  }, [activeTab, selectedStyleIds, selectedPureIds, templates, pureTemplates, reloadIPTemplates]);

  const deleteStyleTemplate = useCallback(async (template: StyleTemplate) => {
    if (!window.confirm(`确认删除模板「${template.name}」？删除后不可恢复。`)) return;
    const previous = templates;
    setTemplates((prev) => prev.filter((t) => t.id !== template.id));
    setSelectedStyleIds((prev) => { const next = new Set(prev); next.delete(template.id); return next; });
    setPreviewStyle((current) => current?.id === template.id ? null : current);
    try {
      const res = await apiFetch(`/faceswap/templates/${template.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
      toast("模板已删除", "success");
      reloadIPTemplates();
    } catch (e) {
      setTemplates(previous);
      toast("删除失败：" + (e instanceof Error ? e.message : String(e)), "error");
      reloadIPTemplates();
    }
  }, [templates, reloadIPTemplates]);

  const batchDelete = useCallback(async () => {
    if (activeTab === "style") {
      const ids = Array.from(selectedStyleIds);
      if (ids.length === 0) return;
      if (!window.confirm(`确认删除选中的 ${ids.length} 个模板？删除后不可恢复。`)) return;
      const previous = templates;
      setTemplates((prev) => prev.filter((t) => !selectedStyleIds.has(t.id)));
      setSelectedStyleIds(new Set());
      setBatchMode(false);
      try {
        for (const id of ids) {
          const res = await apiFetch(`/faceswap/templates/${id}`, { method: "DELETE" });
          if (!res.ok) throw new Error(await res.text());
        }
        toast(`已删除 ${ids.length} 个模板`, "success");
        reloadIPTemplates();
      } catch (e) {
        setTemplates(previous);
        toast("批量删除失败：" + (e instanceof Error ? e.message : String(e)), "error");
        reloadIPTemplates();
      }
      return;
    } else if (activeTab === "pure") {
      setPureTemplates((prev) => prev.filter((t) => !selectedPureIds.has(t.id)));
      setSelectedPureIds(new Set());
    }
    setBatchMode(false);
  }, [activeTab, selectedStyleIds, selectedPureIds, templates, reloadIPTemplates]);
  const selectAll = useCallback(() => {
    if (activeTab === "style") setSelectedStyleIds(new Set(filteredStyleTemplates.map((t) => t.id)));
    else if (activeTab === "pure") setSelectedPureIds(new Set(filteredPureTemplates.map((t) => t.id)));
  }, [activeTab, filteredStyleTemplates, filteredPureTemplates]);

  const currentSelectedCount = activeTab === "style"
    ? selectedStyleIds.size
    : activeTab === "pure" ? selectedPureIds.size : 0;


  useEffect(() => { setBatchMode(false); clearSelection(); }, [activeTab, clearSelection]);

  // All chart series compute from real data: rankings/heat from the actual
  // template store, trend from generation_jobs via /faceswap/trend.
  const chartData = useMemo(() => {
    const styleGroups: Record<string, number> = {};
    templates.forEach((t) => {
      const k = t.styleName || "IP 形象";
      styleGroups[k] = (styleGroups[k] || 0) + (t.generateCount || 0);
    });
    const styleRank = Object.entries(styleGroups)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
    const sceneHeat = templates
      .map((t) => ({ name: t.name, value: t.generateCount || 0 }))
      .sort((a, b) => b.value - a.value).slice(0, 6);
    const trend = timeRange === "today"
      ? { d: realTrend.today, l: "每小时" }
      : timeRange === "7d" ? { d: realTrend.d7, l: "每天" } : { d: realTrend.d30, l: "每天" };
    return { styleRank, sceneHeat, dailyTrend: trend.d, trendLabel: trend.l };
  }, [templates, timeRange, realTrend]);

  const handleEditStyleSave = async (
    updated: ManagedStyleTemplate,
    file?: File | null,
    supplementalItems?: LocalSupplementalItem[],
    actionPromptsTouched?: boolean
  ) => {
    const current = templates.find((t) => t.id === updated.id);
    const enabledChanged = current ? current.enabled !== updated.enabled : false;
    setPreviewStyle(null);
    // Persist real templates (mock AI旅拍 rows keep local-only edits for now).
    if (!String(updated.id).startsWith("pure-")) {
      const fd = new FormData();
      fd.append("name", updated.name);
      fd.append("category", updated.styleId);
      fd.append("subcategory", updated.subcategory || "");
      fd.append("subsubcategory", updated.subsubcategory || "");
      fd.append("style", updated.style || "");
      const provider = templateGenerationProvider(updated);
      fd.append("generation_provider", provider);
      fd.append("scene", updated.scene || "03");
      fd.append("prompt", updated.prompt || "");
      if (file) fd.append("file", file);

      if (provider === "wan") {
        fd.append("outfit_type_tag_id", updated.outfitType?.id || "");
        const filterTagIds = (updated.outfitFilterTags || []).map((t) => t.id).filter(Boolean);
        fd.append("outfit_filter_tag_ids", JSON.stringify(filterTagIds));
        const currentActionPrompts = updated.actionPrompts || [];
        const hasUnavailable = currentActionPrompts.some((p) => !p.enabled);
        if (!hasUnavailable || actionPromptsTouched) {
          const actionPromptIds = currentActionPrompts.map((p) => p.id).filter(Boolean);
          fd.append("action_prompt_ids", JSON.stringify(actionPromptIds));
        }
        if (supplementalItems) {
          const manifest: ({ id: string } | { fileIndex: number })[] = [];
          const supplementalFiles: File[] = [];
          supplementalItems.forEach((item) => {
            if (item.kind === "existing" && item.id) {
              manifest.push({ id: item.id });
            } else if (item.kind === "new" && item.file) {
              manifest.push({ fileIndex: supplementalFiles.length });
              supplementalFiles.push(item.file);
            }
          });
          fd.append("supplemental_manifest", JSON.stringify(manifest));
          supplementalFiles.forEach((f) => fd.append("supplemental_files", f));
        }
      } else {
        fd.append("outfit_type_tag_id", "");
        fd.append("outfit_filter_tag_ids", JSON.stringify([]));
        fd.append("supplemental_manifest", JSON.stringify([]));
        fd.append("action_prompt_ids", JSON.stringify([]));
      }
      const res = await apiFetch(`/faceswap/templates/${updated.id}`, { method: "PUT", body: fd });
      if (!res.ok) throw new Error(await res.text());

      if (enabledChanged) {
        const toggleRes = await apiFetch(`/faceswap/templates/${updated.id}/toggle`, { method: "POST" });
        if (!toggleRes.ok) throw new Error(await toggleRes.text());
      }
      await reloadIPTemplates();
    } else {
      setTemplates((prev) => prev.map((t) => t.id === updated.id ? updated : t));
    }
  };

  const handleEditPureSave = (updated: PureSwapTemplate) => {
    setPureTemplates((prev) => prev.map((t) => t.id === updated.id ? updated : t));
    setPreviewPure(null);
  };

  return (
    <CategoryTreeContext.Provider value={{ tree: catTree, topCategories, subMap, sub2Map, reload: reloadCatTree }}>
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AUGC 换脸</h1>
          <p className="text-sm text-gray-500 mt-1">AI 生成内容换脸模板管理</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "style" && (
            <>
              <button onClick={() => setShowCategoriesManager(true)} className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-2">
                <i className="ri-folder-settings-line" />分类管理
              </button>
              <button onClick={() => setShowBatchUpload(true)} className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-2">
                <i className="ri-upload-2-line" />批量上传
              </button>
              <button onClick={() => setShowCreateTemplate(true)} className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-2">
                <i className="ri-upload-cloud-line" />新建模板
              </button>
            </>
          )}
          {activeTab === "pure" && (
            <button className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-2">
              <i className="ri-upload-cloud-line" />上传场景
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <AnimatedStat value={fsStats.totalTemplates || templates.length} label="模板总数" colorClass="text-gray-900" bgClass="bg-white" borderClass="border-gray-200" delay={0} />
        <AnimatedStat value={fsStats.enabledTemplates || templates.filter((t) => t.enabled).length} label="已启用" colorClass="text-emerald-700" bgClass="bg-emerald-50" borderClass="border-emerald-200" delay={80} />
        <AnimatedStat value={fsStats.todayGenerated} label="今日生成量" colorClass="text-rose-700" bgClass="bg-rose-50" borderClass="border-rose-200" delay={160} />
        <AnimatedStat value={new Set(templates.map((t) => t.styleName)).size} label="分类数" colorClass="text-amber-700" bgClass="bg-amber-50" borderClass="border-amber-200" delay={240} />
        <AnimatedStat value={templates.filter((t) => t.styleName === "卡通").length} label="卡通模板" colorClass="text-teal-700" bgClass="bg-teal-50" borderClass="border-teal-200" delay={320} />
        <AnimatedStat value={templates.length - templates.filter((t) => t.styleName === "卡通").length} label="游记模板" colorClass="text-indigo-700" bgClass="bg-indigo-50" borderClass="border-indigo-200" delay={400} />
      </div>

      {/* Charts */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">数据概览</h2>
          <div className="flex items-center gap-2">
            <TimeFilter value={timeRange} onChange={setTimeRange} />
            <div className="bg-white rounded-xl border border-gray-200 p-1 inline-flex">
              {(["style", "scene"] as const).map((k) => (
                <button key={k} onClick={() => setRankTab(k)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${rankTab === k ? "bg-teal-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"}`}>
                  {k === "style" ? "风格排行" : "场景热度排行"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <BarRankChart data={rankTab === "style" ? chartData.styleRank : chartData.sceneHeat} title={rankTab === "style" ? "风格生成量排行" : "场景热度排行"} />
          <SceneHeatChart data={chartData.sceneHeat} />
          <DailyTrendChart data={chartData.dailyTrend} label={chartData.trendLabel} />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-1.5 inline-flex">
        <button onClick={() => { requestTabChange("style"); setSearchQuery(""); setSelectedSceneType("all"); }} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "style" || activeTab === "pure" ? "bg-teal-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"}`}>
          <i className="ri-palette-line mr-1.5" />模板管理
        </button>
        <button onClick={() => requestTabChange("outfit")} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "outfit" ? "bg-teal-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"}`}>
          <i className="ri-shirt-line mr-1.5" />服装管理
        </button>
        <button onClick={() => requestTabChange("action")} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "action" ? "bg-teal-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"}`}>
          <i className="ri-walk-line mr-1.5" />动作提示词
        </button>
        <button onClick={() => requestTabChange("beauty")} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "beauty" ? "bg-teal-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"}`}>
          <i className="ri-magic-line mr-1.5" />美颜预设
        </button>
        <button onClick={() => requestTabChange("history")} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "history" ? "bg-teal-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"}`}>
          <i className="ri-history-line mr-1.5" />生成记录
        </button>
      </div>

      {/* Filter bar and Batch actions (only for template management tabs) */}
      {(activeTab === "style" || activeTab === "pure") && (
        <>
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
              <span className="text-xs text-gray-500 font-medium mr-1">模板类型:</span>
              <button
                onClick={() => { requestTabChange("style"); setSearchQuery(""); setSelectedSceneType("all"); }}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  activeTab === "style" ? "bg-teal-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                风格模板 (IP形象)
              </button>
              <button
                onClick={() => { requestTabChange("pure"); setSearchQuery(""); setSelectedCategory(null); setSelectedSubcategory(null); }}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  activeTab === "pure" ? "bg-teal-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                场景模板 (AI旅拍)
              </button>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-56">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={activeTab === "style" ? "搜索风格模板" : "搜索换脸场景"} className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
              </div>
              {hasFilters && (
                <button onClick={resetFilters} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-refresh-line" /> 重置
                </button>
              )}
            </div>
            {activeTab === "style" && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500 w-14">一级:</span>
                  <button onClick={() => { setSelectedCategory(null); setSelectedSubcategory(null); setSelectedSubsubcategory(null); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedCategory === null ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>全部</button>
                  {topCategories.map((c) => (
                    <button key={c} onClick={() => { setSelectedCategory(c); setSelectedSubcategory(null); setSelectedSubsubcategory(null); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedCategory === c ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{c}</button>
                  ))}
                </div>
                {selectedCategory && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-500 w-14">二级:</span>
                    <button onClick={() => { setSelectedSubcategory(null); setSelectedSubsubcategory(null); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedSubcategory === null ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>全部</button>
                    {(subMap[selectedCategory] || []).map((sub) => (
                      <button key={sub} onClick={() => { setSelectedSubcategory(sub); setSelectedSubsubcategory(null); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedSubcategory === sub ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{sub}</button>
                    ))}
                  </div>
                )}
                {selectedCategory && selectedSubcategory && (sub2Map[sub2Key(selectedCategory, selectedSubcategory)] || []).length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-500 w-14">三级:</span>
                    <button onClick={() => setSelectedSubsubcategory(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedSubsubcategory === null ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>全部</button>
                    {(sub2Map[sub2Key(selectedCategory, selectedSubcategory)] || []).map((s2) => (
                      <button key={s2} onClick={() => setSelectedSubsubcategory(s2)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedSubsubcategory === s2 ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{s2}</button>
                    ))}
                  </div>
                )}
              </>
            )}
            {activeTab === "pure" && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-500 w-14">场景:</span>
                <button onClick={() => setSelectedSceneType("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedSceneType === "all" ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>全部</button>
                {sceneTypes.map((type) => (
                  <button key={type} onClick={() => setSelectedSceneType(type)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedSceneType === type ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{type}</button>
                ))}
              </div>
            )}
          </div>

          {/* Batch action bar */}
          {batchMode && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-teal-800">已选 {currentSelectedCount} 项</span>
                <button onClick={selectAll} className="text-xs text-teal-600 hover:text-teal-800 font-medium whitespace-nowrap">全选</button>
                <button onClick={clearSelection} className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap">清空</button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => batchEnable(true)} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap flex items-center gap-1"><i className="ri-check-line" /> 批量启用</button>
                <button onClick={() => batchEnable(false)} className="px-3 py-1.5 bg-gray-500 text-white rounded-lg text-xs font-medium hover:bg-gray-600 transition-colors whitespace-nowrap flex items-center gap-1"><i className="ri-forbid-line" /> 批量禁用</button>
                <button onClick={batchDelete} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors whitespace-nowrap flex items-center gap-1"><i className="ri-delete-bin-line" /> 批量删除</button>
                <button onClick={() => { setBatchMode(false); clearSelection(); }} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
              </div>
            </div>
          )}

          {!batchMode && (
            <div className="flex justify-end">
              <button onClick={() => setBatchMode(true)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-1">
                <i className="ri-checkbox-multiple-line" /> 批量操作
              </button>
            </div>
          )}
        </>
      )}
      {/* Style Templates Tab */}
      {activeTab === "style" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">
              {selectedCategory
                ? (selectedSubcategory
                    ? (selectedSubsubcategory
                        ? `${selectedCategory} · ${selectedSubcategory} · ${selectedSubsubcategory}`
                        : `${selectedCategory} · ${selectedSubcategory}`)
                    : selectedCategory)
                : "全部模板"}
            </h2>
            <span className="text-xs text-gray-500">{topCategories.length} 个一级分类 · {filteredStyleTemplates.length} 个模板</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
            {/* Left rail: two-level category tree (top + sub). */}
            <div className="lg:col-span-1 space-y-2">
              {categoryStats.map((c) => {
                const open = selectedCategory === c.cat;
                return (
                  <div key={c.cat} className={`bg-white rounded-xl border ${open ? "border-teal-300 shadow-sm" : "border-gray-200"} overflow-hidden transition-colors`}>
                    <button
                      onClick={() => { if (open) { setSelectedCategory(null); setSelectedSubcategory(null); setSelectedSubsubcategory(null); } else { setSelectedCategory(c.cat); setSelectedSubcategory(null); setSelectedSubsubcategory(null); } }}
                      className={`w-full px-4 py-3 flex items-center justify-between text-left ${open ? "bg-teal-50" : "hover:bg-gray-50"}`}
                    >
                      <span className={`text-sm font-medium ${open ? "text-teal-700" : "text-gray-800"}`}>{c.cat}</span>
                      <span className="text-xs text-gray-500">{c.count} 个</span>
                    </button>
                    {open && (
                      <div className="px-2 pb-2 space-y-1">
                        <button
                          onClick={() => { setSelectedSubcategory(null); setSelectedSubsubcategory(null); }}
                          className={`w-full px-3 py-2 rounded-lg text-xs font-medium text-left flex items-center justify-between ${selectedSubcategory === null ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                        >
                          <span>全部</span><span className="opacity-70">{c.count}</span>
                        </button>
                        {c.subStats.map((s) => {
                          const subActive = selectedSubcategory === s.sub;
                          const hasSub2 = s.sub2Stats.length > 0;
                          return (
                            <div key={s.sub}>
                              <button
                                onClick={() => { setSelectedSubcategory(s.sub); setSelectedSubsubcategory(null); }}
                                className={`w-full px-3 py-2 rounded-lg text-xs font-medium text-left flex items-center justify-between ${subActive ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                              >
                                <span>{s.sub}</span><span className="opacity-70">{s.count}</span>
                              </button>
                              {subActive && hasSub2 && (
                                <div className="pl-4 mt-1 space-y-1">
                                  <button
                                    onClick={() => setSelectedSubsubcategory(null)}
                                    className={`w-full px-3 py-1.5 rounded-lg text-[11px] font-medium text-left flex items-center justify-between ${selectedSubsubcategory === null ? "bg-teal-500/80 text-white" : "text-gray-500 hover:bg-gray-100"}`}
                                  >
                                    <span>全部</span><span className="opacity-70">{s.count}</span>
                                  </button>
                                  {s.sub2Stats.map((s2) => (
                                    <button
                                      key={s2.sub2}
                                      onClick={() => setSelectedSubsubcategory(s2.sub2)}
                                      className={`w-full px-3 py-1.5 rounded-lg text-[11px] font-medium text-left flex items-center justify-between ${selectedSubsubcategory === s2.sub2 ? "bg-teal-500/80 text-white" : "text-gray-500 hover:bg-gray-100"}`}
                                    >
                                      <span>{s2.sub2}</span><span className="opacity-70">{s2.count}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="lg:col-span-3">
              {filteredStyleTemplates.length === 0 ? (
                <div className="text-center py-16 text-gray-400"><i className="ri-image-line text-4xl mb-3 block" /><p className="text-sm">没有找到符合条件的模板</p></div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredStyleTemplates.map((t, i) => (
                    <StyleTemplateCard key={t.id} template={t} onPreview={setPreviewStyle} onEdit={(tpl) => { setEditingStyleTemplate(tpl); }} onDelete={deleteStyleTemplate} onPreviewEffect={(tpl) => setPreviewEffectTemplate(tpl)} onToggleEnabled={toggleStyleEnabled} delay={i * 60} batchMode={batchMode} selected={selectedStyleIds.has(t.id)} onToggleSelect={toggleStyleSelect} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "outfit" && <OutfitLibrary initialOutfits={outfitList.items} groups={outfitTagGroups.length ? outfitTagGroups : outfitList.facets} tags={outfitTags.length ? outfitTags : outfitList.facets.flatMap((group) => group.tags || [])} reloadOutfits={reloadOutfits} reloadTagData={() => { reloadOutfitTagGroups(); reloadOutfitTags(); reloadOutfits(); }} />}

      {activeTab === "action" && (
        <ActionPromptLibrary
          prompts={actionPrompts}
          groups={actionPromptGroups}
          tags={actionPromptTags}
          reloadPrompts={reloadActionPrompts}
          reloadTagData={() => {
            reloadActionPromptGroups();
            reloadActionPromptTags();
            reloadActionPrompts();
          }}
        />
      )}

      {/* Generation history (merged from /augc-history) */}
      {activeTab === "history" && <AugcHistoryPage embedded />}

      {/* Beauty preset editor */}
      {activeTab === "beauty" && <BeautyPresetsTab onRegisterLeaveHandler={registerBeautyLeaveHandler} />}

      {/* Pure Face Swap Tab */}
      {activeTab === "pure" && (
        <div className="space-y-5">
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-100 p-5 flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-xl text-white"><i className="ri-image-edit-line text-xl" /></div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900">AI旅拍大片模板（预留人脸位，上传即出片）</h3>
              <p className="text-xs text-gray-500 mt-0.5">风景大片级视角，画面中预留人脸区域，游客上传照片后自动替换，生成专属旅行大片</p>
            </div>
            <div className="flex gap-4 text-center">
              <div><div className="text-lg font-bold text-teal-700">{augcStats.totalPureScenes}</div><div className="text-[10px] text-gray-500">场景模板</div></div>
              <div><div className="text-lg font-bold text-teal-700">{sceneTypes.length}</div><div className="text-[10px] text-gray-500">场景类型</div></div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-1">
              <HotRank templates={pureTemplates} />
            </div>
            <div className="lg:col-span-3 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">{selectedSceneType === "all" ? "全部场景" : selectedSceneType}</h2>
                <span className="text-xs text-gray-500">{filteredPureTemplates.length} 个场景</span>
              </div>
              {filteredPureTemplates.length === 0 ? (
                <div className="text-center py-16 text-gray-400"><i className="ri-image-line text-4xl mb-3 block" /><p className="text-sm">没有找到符合条件的场景</p></div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPureTemplates.map((t, i) => (
                    <PureSwapTemplateCard key={t.id} template={t} onPreview={setPreviewPure} onUpload={(tpl) => { setFaceSwapTemplate(tpl); setShowFaceSwap(true); }} onEdit={(tpl) => { setEditingPureTemplate(tpl); }} onPreviewEffect={(tpl) => setPreviewEffectTemplate(tpl)} onToggleEnabled={togglePureEnabled} delay={i * 60} batchMode={batchMode} selected={selectedPureIds.has(t.id)} onToggleSelect={togglePureSelect} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drawers & Modals */}
      <StyleDetailDrawer template={previewStyle} onClose={() => setPreviewStyle(null)} onEdit={(tpl) => { setEditingStyleTemplate(tpl); setPreviewStyle(null); }} onDelete={deleteStyleTemplate} onToggleEnabled={toggleStyleEnabled} />
      <PureDetailDrawer template={previewPure} onClose={() => setPreviewPure(null)} onEdit={(tpl) => { setEditingPureTemplate(tpl); setPreviewPure(null); }} onToggleEnabled={togglePureEnabled} />
      <CreateTemplateModal open={showCreateTemplate} onClose={() => setShowCreateTemplate(false)} defaultCategory={selectedCategory} onCreated={reloadIPTemplates} outfitTags={outfitTags} outfitTagGroups={outfitTagGroups} actionPrompts={actionPrompts} />
      <BatchUploadModal open={showBatchUpload} onClose={() => setShowBatchUpload(false)} defaultCategory={selectedCategory} onUploaded={reloadIPTemplates} outfitTags={outfitTags} outfitTagGroups={outfitTagGroups} actionPrompts={actionPrompts} />
      <FaceSwapModal template={faceSwapTemplate} open={showFaceSwap} onClose={() => setShowFaceSwap(false)} />
      <TemplatePreviewModal template={previewEffectTemplate} open={previewEffectTemplate !== null} onClose={() => setPreviewEffectTemplate(null)} />
      <EditStyleTemplateModal template={editingStyleTemplate} open={editingStyleTemplate !== null} onClose={() => setEditingStyleTemplate(null)} onSave={handleEditStyleSave} outfitTags={outfitTags} outfitTagGroups={outfitTagGroups} actionPrompts={actionPrompts} />
      <EditPureTemplateModal template={editingPureTemplate} open={editingPureTemplate !== null} onClose={() => setEditingPureTemplate(null)} onSave={handleEditPureSave} />
      <CategoriesManagerModal open={showCategoriesManager} onClose={() => setShowCategoriesManager(false)} onChanged={() => { reloadCatTree(); reloadIPTemplates(); }} />
    </div>
    </CategoryTreeContext.Provider>
  );
}

export default FaceswapPage;
